// src/share/sharingCenter.jsx
// Global Sharing Center modal - draggable, resizable, with left panel + right panel layout.
// Mirrors designCenter.jsx modal shell.

import React, { useRef, useState, useCallback, useEffect, useMemo } from 'react';
import { Rnd } from 'react-rnd';
import { FaTimes, FaWindowMaximize, FaWindowRestore } from 'react-icons/fa';
import { useModalStore } from '../state/modalStore';
import { useProfileStore } from '../state/profileStore';
import {
  assetUrl,
  BoldHeroPublic,
  GridGalleryPublic,
  FeatureStripPublic,
  CompactEditorialPublic,
  ContactToast,
} from '../design/PublicLandingPage';
import { PROFILE_FRAME_MAP, makeHandle, makeInitials, ProfileShareModal } from './ProfileSharePreview';
import { COMPANY_FRAME_MAP, CompanyShareModal } from './CompanySharePreview';
import { TEAM_FRAME_MAP, TeamShareModal } from './TeamSharePreview';
import { CREATION_FRAME_MAP } from '../design/PublishPreviewModal';
import { PLATFORM_SIGNUP_URLS, PLATFORM_LABELS as LIB_PLATFORM_LABELS } from './platformSignupUrls';
import CustomerPicker from './CustomerPicker';
import { parseEmailList } from './parseEmailList';

const API_URL = process.env.REACT_APP_API_URL || '';
const IMG_CACHE_KEY = Date.now(); // stable per session — images cache within a session, refresh on reload

// Category key → human-readable label
const CATEGORY_LABELS = {
  how_things_work:    'How Things Work',
  how_to_tips:        'How-To & Tips',
  problems_solutions: 'Problems & Solutions',
  advice_insights:    'Advice & Insights',
  specials_discounts: 'Specials & Discounts',
  stories_experiences:'Stories & Experiences',
  community_relations:'Community Relations',
  humor_personality:  'Humor & Personality',
  general:            'General',
};

// Default column definitions (without Profession)
// All possible columns (icon columns are conditionally shown per platform)
const ALL_COLUMNS = [
  { key: 'visited',     tooltip: 'Visited',  width: 28,  minWidth: 26, center: true, noTruncate: true, iconImg: '/images/visited-icon.svg' },
  { key: 'likes',       label: '❤',  tooltip: 'Likes',    width: 28,  minWidth: 26, center: true, noTruncate: true },
  { key: 'comments',    label: '💬', tooltip: 'Comments', width: 28,  minWidth: 26, center: true, noTruncate: true },
  { key: 'shares',      label: '↪',  tooltip: 'Shares',   width: 28,  minWidth: 26, center: true, noTruncate: true },
  { key: 'views',       label: '👁', tooltip: 'Views',    width: 28,  minWidth: 26, center: true, noTruncate: true },
  { key: 'emailed',     tooltip: 'Emailed',  width: 28,  minWidth: 26, center: true, noTruncate: true, iconImg: '/images/email-icon.svg' },
  { key: 'author',      label: 'Author',       width: 170, minWidth: 80 },
  { key: 'title',       label: 'Title',        width: 250, minWidth: 80 },
  { key: 'category',    label: 'Category',      width: 130, minWidth: 60 },
  { key: 'industry',    label: 'Industry',       width: 160, minWidth: 80 },
  { key: 'date',        label: 'Published',     width: 95,  minWidth: 60 },
];

// Which icon columns to show per platform tab
// null/undefined = creations tab (eHandshake icon) — show all
const PLATFORM_VISIBLE_COLUMNS = {
  facebook:  ['visited', 'likes', 'comments', 'shares', 'views', 'author', 'title', 'category', 'industry', 'date'],
  instagram: ['visited', 'likes', 'comments', 'views', 'author', 'title', 'category', 'industry', 'date'],
  twitter:   ['visited', 'likes', 'comments', 'shares', 'views', 'author', 'title', 'category', 'industry', 'date'],
  linkedin:  ['visited', 'author', 'title', 'category', 'industry', 'date'],
  youtube:   ['visited', 'likes', 'comments', 'views', 'author', 'title', 'category', 'industry', 'date'],
  tiktok:    ['visited', 'likes', 'comments', 'shares', 'views', 'author', 'title', 'category', 'industry', 'date'],
  email:     ['emailed', 'author', 'title', 'category', 'industry', 'date'],
};

// Helper to get the active column set
function getColumnsForPlatform(platform) {
  const visibleKeys = PLATFORM_VISIBLE_COLUMNS[platform];
  if (!visibleKeys) return ALL_COLUMNS; // creations tab — show everything
  return ALL_COLUMNS.filter(col => visibleKeys.includes(col.key));
}

// Extract the value to sort by for a given column. Engagement columns
// aggregate across the active platform (or all platforms on the
// creations / email tabs, matching the row render logic). Strings are
// lower-cased so the sort is case-insensitive.
function computeSortValue(post, key, activePlatform) {
  const publishes = post.publishes || {};
  if (key === 'visited') {
    const relevant = activePlatform && activePlatform !== 'email'
      ? (publishes[activePlatform] ? [publishes[activePlatform]] : [])
      : Object.values(publishes);
    return relevant.some(p => p?.viewer_clicked) ? 1 : 0;
  }
  if (key === 'likes' || key === 'comments' || key === 'shares' || key === 'views') {
    const eng = activePlatform && activePlatform !== 'email'
      ? [activePlatform]
      : Object.keys(publishes);
    const field = { likes: 'like_count', comments: 'comment_count', shares: 'share_count', views: 'view_count' }[key];
    let total = 0;
    for (const p of eng) {
      if (publishes[p]) total += publishes[p][field] || 0;
    }
    return total;
  }
  if (key === 'emailed') return Number(post.email_share_count || 0);
  if (key === 'author') return [post.u_first_name, post.u_last_name].filter(Boolean).join(' ').toLowerCase();
  if (key === 'title') return (post.headline || '').toLowerCase();
  if (key === 'category') return (CATEGORY_LABELS[post.category_key] || post.category_key || '').toLowerCase();
  if (key === 'industry') return (post.b_business_industry || '').toLowerCase();
  if (key === 'date') return post.published_at ? new Date(post.published_at).getTime() : 0;
  return 0;
}

// ─── Inline modal for sending a creation via email ───────────────────────────
function CreationEmailModalInline({ post, sharerName, sending, onClose, onSend }) {
  const [to, setTo] = useState('');
  const [subject, setSubject] = useState(`${sharerName} shared a creation with you on eHandshake`);
  const [message, setMessage] = useState('Check out this creation on eHandshake!');
  // Customer multi-select. The extracted CustomerPicker component handles
  // its own search query, dropdown visibility, and customer-list fetch —
  // we just hold the selection array and combine it with the manual "to"
  // field at send time.
  const [selectedCustomers, setSelectedCustomers] = useState([]); // [{customer_id, name, email}]

  // Collect all emails: manual "to" field (comma/semicolon/space-separated)
  // + selected customers.
  const allEmails = [
    ...parseEmailList(to),
    ...selectedCustomers.map(c => c.email),
  ];
  const hasRecipients = allEmails.length > 0;

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center pointer-events-auto" style={{ zIndex: 100000 }}>
      <div className="bg-white rounded-xl shadow-2xl flex flex-col overflow-hidden relative" style={{ width: 500, maxWidth: '94vw', maxHeight: '90vh' }}>
        {/* Header */}
        <div className="shrink-0 flex items-center justify-between px-5 py-3 border-b border-gray-200 bg-gray-50">
          <h3 className="text-sm font-semibold text-gray-800">Email Creation</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-lg leading-none" title="Close">&#10005;</button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {/* Creation title (read-only) */}
          <div className="px-3 py-2 bg-[#f0f9ff] border border-[#00aef7]/20 rounded-lg">
            <div className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-0.5">Creation</div>
            <div className="text-sm font-semibold text-gray-800">{post.headline || '(Untitled)'}</div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">To:</label>
            <input
              type="text"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder="email@example.com, another@example.com"
              className="w-full p-2 border rounded focus:ring-2 focus:ring-[#00aef7] focus:outline-none text-sm"
            />
          </div>

          {/* Customer picker — extracted component, identical behavior */}
          <CustomerPicker
            selectedCustomers={selectedCustomers}
            onChange={setSelectedCustomers}
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Subject:</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="w-full p-2 border rounded focus:ring-2 focus:ring-[#00aef7] focus:outline-none text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Message:</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
              className="w-full p-2 border rounded focus:ring-2 focus:ring-[#00aef7] focus:outline-none text-sm resize-none"
            />
          </div>
        </div>

        {/* Sending overlay — same pattern as ProfileShareModal */}
        {sending && (
          <div className="absolute inset-0 bg-white/80 flex flex-col items-center justify-center z-50 rounded-xl">
            <div className="w-10 h-10 border-4 border-gray-200 border-t-[#00aef7] rounded-full animate-spin mb-3" />
            <p className="text-sm font-medium text-gray-600">Sending email…</p>
            <p className="text-xs text-gray-400 mt-1">Please wait</p>
          </div>
        )}

        {/* Footer */}
        <div className="shrink-0 flex items-center justify-between px-5 py-3 border-t border-gray-200 bg-gray-50">
          <span className="text-xs text-gray-400">
            {allEmails.length > 0 ? `${allEmails.length} recipient${allEmails.length > 1 ? 's' : ''}` : ''}
          </span>
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-100 transition-colors"
              disabled={sending}
            >
              Cancel
            </button>
            <button
              onClick={() => onSend({ recipientEmail: allEmails.join(','), subject, message })}
              disabled={sending || !hasRecipients}
              className="px-5 py-2 bg-[#00aef7] text-white text-sm font-semibold rounded-md hover:bg-[#0095d6] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {sending ? 'Sending...' : allEmails.length > 1 ? `Send to ${allEmails.length} recipients` : 'Send Email'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Inline iframe preview for external URL creations ─────────────────────────
// Checks server-side whether the URL allows iframe embedding. If yes, renders
// an iframe. If not, falls back to the original link card design.
// ─── Open Graph link preview card ─────────────────────────────────────────────
// Instead of iframes (unreliable — most sites block them), we fetch the page's
// Open Graph metadata server-side and render a rich preview card. This is the
// same approach Facebook, Twitter, Slack, Discord, and iMessage use.
function ExternalUrlPreview({ url, authorName }) {
  const [preview, setPreview] = useState(null); // null = loading, { ok, title, description, image, bodyImages, ... }
  const [imgError, setImgError] = useState(false);
  // User-selected override for the preview image. When the page's og:image is
  // a crummy site-wide logo (common on Wix/Squarespace with brand kits), the
  // user can click "Change image" and pick a real body <img> instead. This
  // only affects OUR sharing-center preview — FB/TikTok will still scrape the
  // page's own og:image when the link is actually posted. Fixing that requires
  // updating og:image on the source site.
  const [selectedImage, setSelectedImage] = useState('');
  const [imagePickerOpen, setImagePickerOpen] = useState(false);
  // URLs that failed to load in the picker thumbnails — hidden from the list.
  // Catches URLs the server-side scraper returned but that 404 / CORS-block /
  // otherwise fail to render when the browser actually fetches them.
  const [brokenImages, setBrokenImages] = useState(() => new Set());
  const fullUrl = url.startsWith('http') ? url : `https://${url}`;

  useEffect(() => {
    setPreview(null);
    setImgError(false);
    setSelectedImage('');
    setImagePickerOpen(false);
    setBrokenImages(new Set());
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `${API_URL}/api/social/link-preview?url=${encodeURIComponent(fullUrl)}`,
          { credentials: 'include' }
        );
        const data = await res.json();
        if (!cancelled) setPreview(data);
      } catch {
        if (!cancelled) setPreview({ ok: false });
      }
    })();
    return () => { cancelled = true; };
  }, [fullUrl]);

  const markImageBroken = (u) => {
    setBrokenImages((prev) => {
      if (prev.has(u)) return prev;
      const next = new Set(prev);
      next.add(u);
      return next;
    });
  };

  // Effective image = user override if set, else og:image from the page
  const effectiveImage = selectedImage || preview?.image || '';
  const altImagesRaw = Array.isArray(preview?.bodyImages) ? preview.bodyImages : [];
  const altImages = altImagesRaw.filter((u) => !brokenImages.has(u));
  // Gate button on RAW count so it stays visible even if every alternative
  // fails to load at runtime. If we gated on the filtered count, onError
  // handlers firing during the picker-open paint would collapse the count
  // to zero mid-frame and unmount the button + popover — 500ms of thumbs
  // loading, then everything vanishes.
  const hasImageChoices = effectiveImage && altImagesRaw.length > 0;

  // Loading
  if (!preview) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-[#00aef7] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-xs text-gray-400">Loading preview…</p>
        </div>
      </div>
    );
  }

  // Extract display hostname
  let hostname = '';
  try { hostname = new URL(fullUrl).hostname.replace(/^www\./, ''); } catch {}

  // If OG fetch failed, show a simple link card
  const hasRichData = preview.ok && (preview.title || preview.description || preview.image);

  if (!hasRichData) {
    return (
      <div className="flex items-center justify-center py-10 px-4">
        <div className="text-center max-w-md">
          <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-[#00aef7]/10 flex items-center justify-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-7 h-7 text-[#00aef7]" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 0 1 1.242 7.244l-4.5 4.5a4.5 4.5 0 0 1-6.364-6.364l1.757-1.757m13.35-.622 1.757-1.757a4.5 4.5 0 0 0-6.364-6.364l-4.5 4.5a4.5 4.5 0 0 0 1.242 7.244" />
            </svg>
          </div>
          <p className="text-base text-gray-700 font-medium mb-2">
            {authorName || 'The author'} has provided their own landing page for this post.
          </p>
          <p className="text-sm text-gray-500 mb-5">Click the link below to view it:</p>
          <a
            href={fullUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#00aef7] text-white text-sm font-medium hover:bg-[#0098d8] transition-colors shadow-sm"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
            </svg>
            Visit {hostname || 'landing page'}
          </a>
        </div>
      </div>
    );
  }

  // Rich OG preview card
  return (
    <div className="flex items-start justify-center pt-3 pb-6 px-4">
      <div className="w-full max-w-lg">
        {/* Author attribution */}
        <p className="text-xs text-gray-400 mb-2 text-center">
          {authorName || 'The author'} linked their own landing page for this post.
        </p>

        {/* Card */}
        <a
          href={fullUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="block rounded-xl border border-gray-200 overflow-hidden bg-white hover:shadow-lg transition-shadow group cursor-pointer"
          style={{ textDecoration: 'none' }}
        >
          {/* Preview image (og:image by default; user can swap to a body image) */}
          {effectiveImage && !imgError && (
            <div className="relative w-full bg-gray-100" style={{ maxHeight: 280, overflow: 'hidden' }}>
              <img
                src={effectiveImage}
                alt=""
                className="w-full object-cover group-hover:scale-[1.02] transition-transform duration-300"
                style={{ maxHeight: 280 }}
                onError={() => setImgError(true)}
              />
              {/* "Change image" chip — only if there are body-image alternatives */}
              {hasImageChoices && (
                <>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setImagePickerOpen((v) => !v);
                    }}
                    title="Change preview image"
                    className="absolute top-2 right-2 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#00aef7] hover:bg-[#0098d8] text-white text-xs font-medium shadow-md transition-colors z-20"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L6.832 19.82a4.5 4.5 0 01-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 011.13-1.897L16.863 4.487z" />
                    </svg>
                    Change image
                  </button>

                  {/* Popover attached right under the button — smaller thumbs,
                      small footprint, doesn't get pushed off-screen on narrow
                      platform preview frames. */}
                  {imagePickerOpen && (
                    <div
                      className="absolute top-11 right-2 z-20 bg-white rounded-lg shadow-xl border border-gray-200 p-2 w-[280px]"
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
                    >
                      <div className="flex items-center justify-between mb-1.5 px-1">
                        <p className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold">
                          Pick a preview image
                        </p>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setImagePickerOpen(false);
                          }}
                          className="text-gray-400 hover:text-gray-600 p-0.5 -mr-0.5"
                          title="Close"
                        >
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                      <div className="flex gap-1.5 overflow-x-auto pb-0.5">
                        {/* og:image option (always first) */}
                        {preview.image && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setSelectedImage('');
                              setImgError(false);
                              setImagePickerOpen(false);
                            }}
                            className={`shrink-0 w-12 h-12 rounded-md overflow-hidden border-2 transition-all ${
                              !selectedImage ? 'border-[#00aef7] ring-1 ring-[#00aef7]/30' : 'border-gray-200 hover:border-gray-400'
                            }`}
                            title="Default (og:image from the page)"
                          >
                            <img src={preview.image} alt="" className="w-full h-full object-cover" />
                          </button>
                        )}
                        {altImages.map((imgUrl) => (
                          <button
                            key={imgUrl}
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setSelectedImage(imgUrl);
                              setImgError(false);
                              setImagePickerOpen(false);
                            }}
                            className={`shrink-0 w-12 h-12 rounded-md overflow-hidden border-2 transition-all ${
                              selectedImage === imgUrl ? 'border-[#00aef7] ring-1 ring-[#00aef7]/30' : 'border-gray-200 hover:border-gray-400'
                            }`}
                            title={imgUrl}
                          >
                            <img
                              src={imgUrl}
                              alt=""
                              className="w-full h-full object-cover"
                              onError={() => markImageBroken(imgUrl)}
                            />
                          </button>
                        ))}
                        {/* All alts unloadable — inform the user instead of
                            leaving an awkward lone og:image tile */}
                        {altImagesRaw.length > 0 && altImages.length === 0 && (
                          <p className="text-[10px] text-gray-500 italic self-center pl-1 pr-2">
                            No other images from this page could be loaded.
                          </p>
                        )}
                      </div>
                      <p className="text-[9px] text-gray-400 mt-1.5 px-1 leading-snug">
                        Changes this preview only. Facebook, TikTok, etc. still fetch the page's
                        own og:image when you post the link.
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Text content */}
          <div className="p-5">
            {/* Site name / hostname */}
            <div className="flex items-center gap-2 mb-2">
              {preview.favicon && (
                <img
                  src={preview.favicon}
                  alt=""
                  className="w-4 h-4 rounded-sm"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              )}
              <span className="text-xs text-gray-400 uppercase tracking-wide font-medium">
                {preview.siteName || hostname}
              </span>
            </div>

            {/* Title */}
            {preview.title && (
              <h3 className="text-base font-semibold text-gray-900 mb-1.5 leading-snug group-hover:text-[#00aef7] transition-colors line-clamp-2">
                {preview.title}
              </h3>
            )}

            {/* Description */}
            {preview.description && (
              <p className="text-sm text-gray-500 leading-relaxed line-clamp-3">
                {preview.description}
              </p>
            )}

            {/* URL */}
            <div className="flex items-center gap-1.5 mt-3 text-xs text-gray-400">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 0 1 1.242 7.244l-4.5 4.5a4.5 4.5 0 0 1-6.364-6.364l1.757-1.757m13.35-.622 1.757-1.757a4.5 4.5 0 0 0-6.364-6.364l-4.5 4.5a4.5 4.5 0 0 0 1.242 7.244" />
              </svg>
              <span className="truncate">{fullUrl}</span>
            </div>
          </div>
        </a>

        {/* Open in new tab button below card */}
        <div className="text-center mt-4">
          <a
            href={fullUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-xs text-[#00aef7] hover:text-[#0087c7] font-medium hover:underline"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
            </svg>
            Open in new tab
          </a>
        </div>
      </div>
    </div>
  );
}

// ─── Search helpers (case-insensitive substring match) ──────────────────────
// Used by the shared search box at the top of profile / company / teams
// sections. Match against name, industry, company name, city, state.
function memberMatchesSearchQuery(member, query) {
  if (!member) return false;
  const q = String(query || '').trim().toLowerCase();
  if (!q) return true;
  const fields = [
    member.u_first_name,
    member.u_last_name,
    `${member.u_first_name || ''} ${member.u_last_name || ''}`.trim(),
    member.b_business_industry,
    member.b_name,
    member.b_city,
    member.b_state,
  ];
  return fields.some((f) => String(f || '').toLowerCase().includes(q));
}

// Walks teamsWithMembers, returns flat array of unique members matching
// the query. Deduped by u_id. Each result gets _team_ctx set to the first
// team it was found in, so the contact card can render captain styling.
function flattenMatchingMembers(teamsWithMembers, query) {
  const seen = new Map();
  for (const team of teamsWithMembers || []) {
    for (const m of team.members || []) {
      if (!m || seen.has(m.u_id)) continue;
      if (memberMatchesSearchQuery(m, query)) {
        seen.set(m.u_id, {
          ...m,
          _team_ctx: { team_id: team.team_id, team_name: team.team_name, captain_u_id: team.captain_u_id },
        });
      }
    }
  }
  return Array.from(seen.values());
}

// Like flattenMatchingMembers but dedupes by company (b_id, falling back
// to b_name) so the company section shows one card per company even when
// multiple teammates work for the same business.
function flattenMatchingCompanies(teamsWithMembers, query) {
  const seen = new Map();
  for (const team of teamsWithMembers || []) {
    for (const m of team.members || []) {
      if (!m) continue;
      const key = m.b_id != null ? `id:${m.b_id}` : m.b_name ? `name:${m.b_name}` : `u:${m.u_id}`;
      if (seen.has(key)) continue;
      if (memberMatchesSearchQuery(m, query)) {
        seen.set(key, m);
      }
    }
  }
  return Array.from(seen.values());
}

export default function SharingCenter() {
  const { sharingCenterOpen, closeSharingCenter } = useModalStore();
  const sharingCenterPending = useModalStore((s) => s.sharingCenterPending);
  const sharingCenterModerationMsg = useModalStore((s) => s.sharingCenterModerationMsg);
  const setSharingCenterModerationMsg = useModalStore((s) => s.setSharingCenterModerationMsg);
  const bringToFront = useModalStore((s) => s.bringToFront);
  const sharingCenterZ = useModalStore((s) => s.getZIndex('sharingCenter'));
  const openCandidate = useModalStore((s) => s.openCandidate);

  const modalRef = useRef(null);
  const [windowMode, setWindowMode] = useState('norm');
  const [activePlatform, setActivePlatform] = useState('facebook');
  const [activeSection, setActiveSection] = useState('creations');
  // Auto-switch away from LinkedIn when moving to profile/company/team sections
  useEffect(() => {
    if (activeSection !== 'creations' && activePlatform === 'linkedin') {
      setActivePlatform('facebook');
    }
  }, [activeSection, activePlatform]);
  const [posts, setPosts] = useState([]);
  const [showMyPostsOnly, setShowMyPostsOnly] = useState(false);
  const [postsLoading, setPostsLoading] = useState(false);
  // Shared search across profile/company/teams sections. When non-empty,
  // each section pivots to a flat-grid render of matching cards. The
  // existing team-grouped DOM stays mounted and is hidden via CSS — no
  // unmount/remount, no risk of breaking click handlers like last time.
  const [searchQuery, setSearchQuery] = useState('');
  const [creationEmailModal, setCreationEmailModal] = useState(null); // viewingPost when email modal is open
  const [isFreeAgent, setIsFreeAgent] = useState(false); // true when user has no active team memberships
  const [myExchangePosts, setMyExchangePosts] = useState([]); // Free Agent's own Exchange submissions

  // ── Social connection state ────────────────────────────────────────────
  const [socialConnections, setSocialConnections] = useState(null);  // from GET /api/social/status
  const [sharerMessage, setSharerMessage] = useState('');            // mandatory personal message for social posts
  const [socialConnecting, setSocialConnecting] = useState(false);   // true while OAuth popup is open
  const oauthPopupRef = useRef(null);                                // ref to OAuth popup window
  const [showPostModal, setShowPostModal] = useState(false);         // Facebook post preview modal
  const [selectedPageId, setSelectedPageId] = useState(null);        // which Facebook page to post to

  // ── Creation delete state ────────────────────────────────────────────
  const [actionMenuPostId, setActionMenuPostId] = useState(null);
  const [confirmDeletePostId, setConfirmDeletePostId] = useState(null);
  const [deletingPostId, setDeletingPostId] = useState(null);

  // ── Profile sharing: teams + members ─────────────────────────────────
  const { profile } = useProfileStore();
  const [teamsWithMembers, setTeamsWithMembers] = useState([]);
  const [teamsLoading, setTeamsLoading] = useState(false);
  const [localContacts, setLocalContacts] = useState([]);    // FA/FS: team members within 100mi
  const [collapsedTeams, setCollapsedTeams] = useState({});
  const [selectedMember, setSelectedMember] = useState(null);
  const [shareModalMember, setShareModalMember] = useState(null); // member object when share modal is open
  const [hoveredMember, setHoveredMember] = useState(null); // { member, rect }
  const [cardPopupMember, setCardPopupMember] = useState(null); // FA/FS: intermediate popup on card click
  const hoverTimerRef = useRef(null);

  // ── Cooldown state — tracks which (target, platform) combos the viewer already shared ──
  const [cooldowns, setCooldowns] = useState({ profiles: {}, companies: {}, teams: {} });
  // Which card's cooldown popup is currently open. Shape: { kind, id }
  // where kind is 'profile' | 'company' | 'team' and id is u_id or team_id.
  // Only one popup can be open at a time.
  const [cooldownPopup, setCooldownPopup] = useState(null);
  const [optedOutUsers, setOptedOutUsers] = useState(new Set());

  // ── Company sharing state ──────────────────────────────────────────────
  const [companyShareModalMember, setCompanyShareModalMember] = useState(null);

  // ── Team sharing state ─────────────────────────────────────────────────
  const [shareModalTeam, setShareModalTeam] = useState(null); // team object when team share modal is open
  const [hoveredTeam, setHoveredTeam] = useState(null); // { team, rect }
  const teamHoverTimerRef = useRef(null);

  // Sharing user's biz info (for platform frame header)
  const sharerBiz = useMemo(() => ({
    name: profile?.b_name || 'eHandshake User',
    handle: makeHandle(profile?.b_name),
    initials: makeInitials(profile?.b_name),
    location: [profile?.b_city, profile?.b_state].filter(Boolean).join(', '),
  }), [profile?.b_name, profile?.b_city, profile?.b_state]);

  const onCardMouseEnter = useCallback((member, e) => {
    clearTimeout(hoverTimerRef.current);
    const rect = e.currentTarget.getBoundingClientRect();
    hoverTimerRef.current = setTimeout(() => {
      setHoveredMember({ member, rect });
    }, 300); // small delay to avoid flicker
  }, []);

  const onCardMouseLeave = useCallback(() => {
    clearTimeout(hoverTimerRef.current);
    setHoveredMember(null);
  }, []);

  // Team card hover handlers (for teams section)
  const onTeamCardMouseEnter = useCallback((team, e) => {
    clearTimeout(teamHoverTimerRef.current);
    const rect = e.currentTarget.getBoundingClientRect();
    teamHoverTimerRef.current = setTimeout(() => {
      setHoveredTeam({ team, rect });
    }, 300);
  }, []);

  const onTeamCardMouseLeave = useCallback(() => {
    clearTimeout(teamHoverTimerRef.current);
    setHoveredTeam(null);
  }, []);

  // Creation hover no longer shows a floating preview — the platform view
  // now lives inside the creation detail page as a top section.
  // Keep no-op handlers so the existing call sites don't blow up.
  const onCreationMouseEnter = useCallback(() => {}, []);
  const onCreationMouseLeave = useCallback(() => {}, []);

  const toggleTeamCollapse = useCallback((teamId) => {
    setCollapsedTeams((prev) => ({ ...prev, [teamId]: !prev[teamId] }));
  }, []);

  const fetchTeamsAndMembers = useCallback(async () => {
    if (!profile?.u_id) return;
    setTeamsLoading(true);
    try {
      // Determine FA status: check if user has any active team memberships
      let isNonTeamUser = isFreeAgent;
      if (!freeAgentChecked.current) {
        try {
          const checkRes = await fetch(`/api/teams/recruiting/debug/my-teams?u_id=${profile.u_id}`, { credentials: 'include' });
          const checkData = await checkRes.json();
          isNonTeamUser = (checkData?.teams || []).length === 0;
          setIsFreeAgent(isNonTeamUser);
          freeAgentChecked.current = true;
        } catch { isNonTeamUser = false; }
      }

      if (isNonTeamUser) {
        // FA/FS: fetch nearby teams with all their members
        const res = await fetch(`${API_URL}/api/exchange/local-contacts`, { credentials: 'include' });
        const data = await res.json();
        // Normalize rating field names from local-contacts (u_avg_rating → rating_avg)
        const normalizedTeams = (data.teams || []).map(t => ({
          ...t,
          members: (t.members || []).map(m => ({
            ...m,
            rating_avg: m.rating_avg ?? m.u_avg_rating ?? null,
            rating_count: m.rating_count ?? m.u_review_count ?? 0,
          })),
        }));
        setTeamsWithMembers(data.ok ? normalizedTeams : []);
        setLocalContacts([]);
        // First team open, rest collapsed
        const collapsed = {};
        (data.teams || []).forEach((t, idx) => {
          if (idx > 0) collapsed[t.team_id] = true;
        });
        setCollapsedTeams(collapsed);
      } else {
        // Team member: fetch own teams + members
        const teamsRes = await fetch(`/api/teams/recruiting/debug/my-teams?u_id=${profile.u_id}`, { credentials: 'include' });
        const teamsData = await teamsRes.json();
        const teams = teamsData?.teams || [];

        const teamsWithMembersData = await Promise.all(
          teams.map(async (team) => {
            try {
              const idsRes = await fetch(`/api/teams/members/ids?team_id=${team.team_id}`, { credentials: 'include' });
              const idsData = await idsRes.json();
              const uIds = idsData?.u_ids || [];
              if (uIds.length === 0) return { ...team, members: [] };
              const usersRes = await fetch(`/api/teams/users/by-ids?ids=${uIds.join(',')}`, { credentials: 'include' });
              const usersData = await usersRes.json();
              const members = usersData?.users || [];
              return { ...team, members };
            } catch (err) {
              console.error('[SharingCenter] fetch team members error:', err);
              return { ...team, members: [] };
            }
          })
        );
        setTeamsWithMembers(teamsWithMembersData);
        setLocalContacts([]);
        const collapsed = {};
        teamsWithMembersData.forEach((t, idx) => {
          if (idx > 0) collapsed[t.team_id] = true;
        });
        setCollapsedTeams(collapsed);
      }
    } catch (err) {
      console.error('[SharingCenter] fetch teams error:', err);
    }
    setTeamsLoading(false);
  }, [profile?.u_id, isFreeAgent]);

  useEffect(() => {
    if (sharingCenterOpen && (activeSection === 'profile' || activeSection === 'company' || activeSection === 'teams')) {
      fetchTeamsAndMembers();
    }
  }, [sharingCenterOpen, activeSection, fetchTeamsAndMembers]);

  // ── Fetch cooldown/opt-out data when platform or section changes ────────
  useEffect(() => {
    if (!sharingCenterOpen || !activePlatform || activePlatform === 'email') return;
    if (activeSection !== 'profile' && activeSection !== 'company' && activeSection !== 'teams') return;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/sharing/cooldowns?platform=${activePlatform}`, { credentials: 'include' });
        const data = await res.json();
        if (data.ok) {
          setCooldowns(data.cooldowns || { profiles: {}, companies: {}, teams: {} });
          setOptedOutUsers(new Set((data.opted_out || []).map(Number)));
        }
      } catch (err) {
        console.error('[SharingCenter] cooldown fetch error:', err);
      }
    })();
  }, [sharingCenterOpen, activePlatform, activeSection]);

  // ── Fetch social connection status ──────────────────────────────────────
  const fetchSocialStatus = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/social/status`, { credentials: 'include' });
      const data = await res.json();
      if (data.ok) setSocialConnections(data.connections);
    } catch (err) {
      console.error('[SharingCenter] fetch social status error:', err);
    }
  }, []);

  useEffect(() => {
    if (sharingCenterOpen) fetchSocialStatus();
  }, [sharingCenterOpen, fetchSocialStatus]);

  // ── Trigger engagement count refresh when Sharing Center opens, then re-fetch posts ──
  useEffect(() => {
    if (!sharingCenterOpen) return;
    (async () => {
      try {
        // Wait for engagement poll to complete (server-side)
        await fetch(`${API_URL}/api/designcenter/sharing-center/refresh-engagement`, {
          method: 'POST', credentials: 'include',
        });
        // Re-fetch posts so the grid shows updated counts
        if (fetchPostsRef.current) fetchPostsRef.current();
      } catch {}
    })();
  }, [sharingCenterOpen]);

  // ── Ref: auto-post after OAuth connect completes ──
  const postAfterConnectRef = useRef(false);

  // ── Detect OAuth popup closed — refresh status in case postMessage was lost ──
  useEffect(() => {
    if (!socialConnecting) return;
    const interval = setInterval(() => {
      if (oauthPopupRef.current && oauthPopupRef.current.closed) {
        oauthPopupRef.current = null;
        setSocialConnecting(false);
        // Don't reset postAfterConnectRef here — the postMessage handler may still fire.
        // Instead, refresh status. If user actually connected, we'll detect it.
        fetchSocialStatus().then(() => {
          // If postAfterConnectRef is still true and we're now connected, auto-post
          if (postAfterConnectRef.current) {
            postAfterConnectRef.current = false;
            document.dispatchEvent(new CustomEvent('social-auto-post'));
          }
        });
      }
    }, 500);
    return () => clearInterval(interval);
  }, [socialConnecting, fetchSocialStatus]);

  // ── Listen for OAuth popup postMessage (after user connects Facebook) ──
  useEffect(() => {
    function handleOAuthMessage(e) {
      if (e.data?.type === 'social-auth-callback') {
        setSocialConnecting(false);
        oauthPopupRef.current = null;
        if (e.data.data?.ok) {
          const shouldAutoPost = postAfterConnectRef.current;
          postAfterConnectRef.current = false;
          const returnedPages = e.data.data?.pages || [];

          // Refresh connection state, THEN auto-post if appropriate
          fetchSocialStatus().then(() => {
            if (!shouldAutoPost) return;
            if (returnedPages.length > 1) {
              // Multiple pages — don't auto-post, let user choose in the modal
              console.log('[SharingCenter] Multiple pages connected — user must choose before posting');
            } else {
              // Single page — connection is confirmed saved, safe to auto-post
              document.dispatchEvent(new CustomEvent('social-auto-post'));
            }
          });
        }
      }
    }
    window.addEventListener('message', handleOAuthMessage);
    return () => window.removeEventListener('message', handleOAuthMessage);
  }, [fetchSocialStatus]);

  // ── Inline post view (replaces grid when a row is clicked) ────────────
  const [viewingPost, setViewingPost] = useState(null);       // the post row from the grid
  const [landingPageData, setLandingPageData] = useState(null); // full landing page data
  const [lpLoading, setLpLoading] = useState(false);
  const [showLpContact, setShowLpContact] = useState(false);

  const openPostView = useCallback(async (post) => {
    setSharerMessage('');     // reset sharer message for new post
    setShowFreeAgentToast(false); // reset
    setViewingPost(post);
    // Free Agent self-post toast DISABLED for pre-launch — all users can post their own creations
    // const isOwn = post && Number(post.post_u_id) === Number(profile?.u_id);
    // if (isFreeAgent && isOwn) {
    //   setTimeout(() => setShowFreeAgentToast(true), 300);
    // }
    if (!post.landing_page_slug) {
      setLandingPageData(null);
      return;
    }
    setLpLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/designcenter/public/landing/${post.landing_page_slug}`);
      const data = await res.json();
      if (data.ok && data.landingPage) {
        setLandingPageData(data.landingPage);
      } else {
        setLandingPageData(null);
      }
    } catch (err) {
      console.error('[SharingCenter] fetch landing page error:', err);
      setLandingPageData(null);
    }
    setLpLoading(false);
  }, [profile?.u_id, isFreeAgent]);

  const closePostView = useCallback(() => {
    setViewingPost(null);
    setLandingPageData(null);
    setShowLpContact(false);
    setShowPostModal(false);
    setSharerMessage('');
    setShowFreeAgentToast(false);
  }, []);

  // ── Column sort for the creations grid ──────────────────────────────
  // Click a header → sort asc; click the same header again → desc; click
  // a different header → asc on that column. Null sortBy means use the
  // server-returned order (newest first).
  const [sortBy, setSortBy] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const toggleSort = useCallback((key) => {
    if (sortBy === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(key);
      setSortDir('asc');
    }
  }, [sortBy]);

  // ── Share / Post a creation to a platform (one-time only) ─────────────
  const [shareLoading, setShareLoading] = useState(false);
  const [postError, setPostError] = useState('');
  const [postSuccess, setPostSuccess] = useState('');  // green confirmation toast
  const [showFreeAgentToast, setShowFreeAgentToast] = useState(false);
  const [copyLinkFeedback, setCopyLinkFeedback] = useState(null); // post_id that was just copied
  const [requestingPublish, setRequestingPublish] = useState(null); // { post_id, platform }
  const [requestSuccess, setRequestSuccess] = useState('');

  // Which platforms have real API posting (vs. just mark-as-shared)
  const SOCIAL_PLATFORMS = ['facebook', 'twitter', 'youtube', 'linkedin', 'instagram', 'tiktok'];

  const handleSharePost = useCallback(async () => {
    if (!viewingPost || !activePlatform) return;
    setShareLoading(true);
    try {
      // ── Social platforms: post + mark-as-shared in one server call ──
      if (SOCIAL_PLATFORMS.includes(activePlatform)) {
        const link = viewingPost.landing_page_slug
          ? `${window.location.origin}/p/${viewingPost.landing_page_slug}`
          : viewingPost.external_url || undefined;

        const imageAssetId = viewingPost.image_asset_id || undefined;

        const captions = viewingPost.captions_json;
        const caption = typeof captions === 'string'
          ? JSON.parse(captions)?.[activePlatform]
          : captions?.[activePlatform];

        const creatorName = [viewingPost.u_first_name, viewingPost.u_last_name].filter(Boolean).join(' ');

        const socialRes = await fetch(`${API_URL}/api/social/${activePlatform}/post`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            sharerMessage: sharerMessage.trim(),
            caption: caption || undefined,
            creatorName: creatorName || undefined,
            creatorBizName: viewingPost.b_name || undefined,
            creatorUrlName: viewingPost.u_url_name || undefined,
            creatorBizWebsite: viewingPost.b_website || undefined,
            link,
            imageAssetId,
            postId: viewingPost.post_id, // server marks it as shared too
            pageId: selectedPageId || undefined, // which page to post to (multi-page support)
          }),
        });

        // Guard against non-JSON responses (e.g. proxy HTML error pages)
        let socialData;
        try {
          socialData = await socialRes.json();
        } catch {
          setPostError(`Server returned ${socialRes.status}. Please try again.`);
          setShareLoading(false);
          return;
        }

        if (!socialData.ok) {
          setPostError(socialData.error || 'Failed to post to ' + activePlatform);
          setShareLoading(false);
          return;
        }

        // Success — mark this platform as consumed (post stays for other platforms)
        const platformLabel = activePlatform === 'twitter' ? 'X' : activePlatform.charAt(0).toUpperCase() + activePlatform.slice(1);
        setPosts((prev) => prev.map((p) =>
          p.post_id === viewingPost.post_id
            ? { ...p, shared_platforms: [...(p.shared_platforms || []), activePlatform] }
            : p
        ));
        setSharerMessage('');
        setShowPostModal(false);
        closePostView();
        setPostSuccess(`Successfully posted to ${platformLabel}!`);
        setTimeout(() => setPostSuccess(''), 5000);
        // Re-fetch posts so the grid shows the new publish + visited icon
        if (fetchPostsRef.current) fetchPostsRef.current();

      } else {
        // ── Non-social platforms: just mark as shared via designcenter ──
        const token = localStorage.getItem('token');
        const res = await fetch(
          `${API_URL}/api/designcenter/sharing-center/posts/${viewingPost.post_id}/share`,
          {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
            body: JSON.stringify({ platform: activePlatform }),
          }
        );
        const data = await res.json();
        if (data.ok) {
          setPosts((prev) => prev.filter((p) => p.post_id !== viewingPost.post_id));
          setSharerMessage('');
          setShowPostModal(false);
          closePostView();
        } else {
          setPostError(data.error || 'Failed to share post');
        }
      }
    } catch (err) {
      console.error('[SharingCenter] share error:', err);
      setPostError('Failed to share post. Please try again.');
    }
    setShareLoading(false);
  }, [viewingPost, activePlatform, sharerMessage, selectedPageId, closePostView]);

  // ── Auto-post listener (fires after OAuth connect when user had clicked Post) ──
  useEffect(() => {
    const handler = () => handleSharePost();
    document.addEventListener('social-auto-post', handler);
    return () => document.removeEventListener('social-auto-post', handler);
  }, [handleSharePost]);

  // ── Send creation via email (opens modal first, then this sends) ───────
  const [creationEmailSending, setCreationEmailSending] = useState(false);
  const fetchPostsRef = useRef(null); // ref to fetchPosts for use in handlers defined before it

  const handleCreationEmailSend = useCallback(async ({ recipientEmail, subject, message }) => {
    if (!creationEmailModal) return;
    setCreationEmailSending(true);
    try {
      const res = await fetch(`${API_URL}/api/sharing/creation/email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          postId: creationEmailModal.post_id,
          recipientEmail,
          subject,
          message,
        }),
      });
      const data = await res.json();
      if (data.ok) {
        // Email shares are unlimited — post stays in the grid.
        // Close the email modal and refresh posts to update the email count.
        setCreationEmailModal(null);
        if (fetchPostsRef.current) fetchPostsRef.current();
        setPostSuccess('Email sent successfully!');
        setTimeout(() => setPostSuccess(''), 4000);
      } else {
        setPostError(data.error || 'Failed to send email');
      }
    } catch (err) {
      console.error('[SharingCenter] creation email error:', err);
      setPostError('Failed to send email. Please try again.');
    }
    setCreationEmailSending(false);
  }, [creationEmailModal, closePostView]);

  // ── Active columns based on selected platform ──────────────────────────
  const activeColumns = useMemo(() => getColumnsForPlatform(activePlatform), [activePlatform]);

  // ── Resizable column widths (Excel-like) ──────────────────────────────
  const [colWidths, setColWidths] = useState(() => activeColumns.map((c) => c.width));
  // Force-reset column widths when active columns change (platform switch)
  useEffect(() => {
    setColWidths(activeColumns.map((c) => c.width));
  }, [activeColumns]);
  const dragRef = useRef(null);       // tracks active column-resize drag
  const headerRowRef = useRef(null);  // ref to header row for auto-fit measurement

  // Build the CSS grid-template-columns string from current widths
  const gridTemplateCols = colWidths.map((w) => `${w}px`).join(' ');

  // ── Mouse-drag to resize a column ──────────────────────────────────────
  const onResizeMouseDown = useCallback((e, colIndex) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startW = colWidths[colIndex] || 100;

    const onMouseMove = (ev) => {
      const delta = ev.clientX - startX;
      setColWidths((prev) => {
        const next = [...prev];
        const minW = activeColumns[colIndex].minWidth || 40;
        next[colIndex] = Math.max(minW, startW + delta);
        return next;
      });
    };
    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      dragRef.current = null;
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    dragRef.current = colIndex;
  }, [colWidths]);

  // ── Double-click to auto-fit column width to content ──────────────────
  const onResizeDoubleClick = useCallback((colIndex) => {
    // Measure all cells in this column and pick the widest
    const container = headerRowRef.current?.parentElement;
    if (!container) return;

    // Gather all rows (header + data rows)
    const rows = container.querySelectorAll('[data-grid-row]');
    let maxW = activeColumns[colIndex].minWidth || 40;

    rows.forEach((row) => {
      const cell = row.children[colIndex];
      if (!cell) return;
      // Temporarily remove truncation to measure natural width
      const prev = cell.style.overflow;
      const prevW = cell.style.width;
      cell.style.overflow = 'visible';
      cell.style.width = 'auto';
      const natural = cell.scrollWidth + 16; // 16px padding buffer
      cell.style.overflow = prev;
      cell.style.width = prevW;
      if (natural > maxW) maxW = natural;
    });

    setColWidths((prev) => {
      const next = [...prev];
      next[colIndex] = Math.min(maxW, 500); // cap at 500px
      return next;
    });
  }, []);

  // Check Free Agent status and fetch posts in one flow to avoid race conditions
  const freeAgentChecked = useRef(false);

  const fetchPosts = useCallback(async () => {
    setPostsLoading(true);
    try {
      // Determine Free Agent status first (if not yet checked this session)
      let freeAgent = isFreeAgent;
      if (!freeAgentChecked.current && profile?.u_id) {
        try {
          const teamRes = await fetch(`/api/teams/recruiting/debug/my-teams?u_id=${profile.u_id}`, { credentials: 'include' });
          const teamData = await teamRes.json();
          freeAgent = (teamData?.teams || []).length === 0;
          setIsFreeAgent(freeAgent);
          freeAgentChecked.current = true;
        } catch {
          freeAgent = false;
        }
      }

      // Single unified endpoint for both Team Members and FA / First String —
      // backend handles visibility differences. Each row is one creation with
      // a `publishes` map keyed by platform.
      const res = await fetch(`${API_URL}/api/designcenter/sharing-center/posts`, { credentials: 'include' });
      const data = await res.json();
      console.log('[SharingCenter] fetchPosts response:', { status: res.status, ok: data?.ok, postsCount: data?.posts?.length, error: data?.error, freeAgent });
      if (data.ok) setPosts(data.posts || []);
      setMyExchangePosts([]);
    } catch (err) {
      console.error('[SharingCenter] fetch posts error:', err);
    }
    setPostsLoading(false);
  }, [isFreeAgent, profile?.u_id]);

  // Keep ref in sync so handlers defined before fetchPosts can call it
  fetchPostsRef.current = fetchPosts;

  // Reset Free Agent check when Sharing Center closes, fetch when it opens
  useEffect(() => {
    if (sharingCenterOpen) {
      freeAgentChecked.current = false;
      fetchPosts();
    }
  }, [sharingCenterOpen, fetchPosts]);

  // Close action menu on outside click
  useEffect(() => {
    if (actionMenuPostId == null) return;
    const onMouseDown = () => setActionMenuPostId(null);
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [actionMenuPostId]);

  // Delete a creation (soft delete)
  const handleDeletePost = useCallback(async (postId) => {
    setDeletingPostId(postId);
    try {
      const res = await fetch(`${API_URL}/api/designcenter/sharing-center/posts/${postId}`, { method: 'DELETE', credentials: 'include' });
      const data = await res.json();
      if (data.ok) {
        setPosts(prev => prev.filter(p => p.post_id !== postId));
      }
    } catch (err) {
      console.error('[SharingCenter] delete post error:', err);
    }
    setConfirmDeletePostId(null);
    setDeletingPostId(null);
  }, []);

  // ── Upgrade banner state (shows after each publish for Free Agents / First String) ──
  const [showUpgradeBanner, setShowUpgradeBanner] = useState(false);

  // Open LemonSqueezy checkout as overlay (stays on eHandshake)
  const openUpgradeCheckout = useCallback(async (tier = 'first_string', interval = 'month') => {
    try {
      const res = await fetch(`${API_URL}/api/subscription/create-checkout`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tier, interval }),
      });
      const data = await res.json();
      if (data.ok && data.url) {
        // Use Lemon.js overlay if available, otherwise fall back to new tab
        if (window.LemonSqueezy?.Url?.Open) {
          window.LemonSqueezy.Url.Open(data.url);
        } else {
          window.open(data.url, '_blank');
        }
      } else {
        console.error('[SharingCenter] checkout error:', data.error);
      }
    } catch (err) {
      console.error('[SharingCenter] checkout error:', err);
    }
  }, []);

  // Re-fetch when pending flips from true → false (publish just completed).
  // Use fetchPostsRef so this effect's deps are limited to the pending flag —
  // otherwise fetchPosts changing identity (it depends on isFreeAgent, which
  // gets set inside fetchPosts itself) would re-run this effect and the
  // ref-based "previous" comparison can miss the true→false transition.
  const prevPending = useRef(sharingCenterPending);
  useEffect(() => {
    if (prevPending.current && !sharingCenterPending) {
      if (fetchPostsRef.current) fetchPostsRef.current();
    }
    prevPending.current = sharingCenterPending;
  }, [sharingCenterPending]);

  // Belt-and-suspenders: any code that publishes a creation can dispatch
  // `sharing-center-refresh` on document and we'll re-fetch immediately.
  // This is independent of the pending toggle so a missed transition doesn't
  // leave a stale grid behind.
  useEffect(() => {
    const handler = () => {
      if (fetchPostsRef.current) fetchPostsRef.current();
    };
    document.addEventListener('sharing-center-refresh', handler);
    return () => document.removeEventListener('sharing-center-refresh', handler);
  }, []);

  const [windowRect, setWindowRect] = useState(() => {
    const pad = 12;
    const w = Math.min(900, Math.floor(window.innerWidth * 0.85));
    // Default height = 85% viewport + 100px, clamped so it fits with padding
    const maxH = window.innerHeight - pad * 2;
    const h = Math.min(Math.floor(window.innerHeight * 0.85) + 100, maxH);
    return {
      width: w,
      height: h,
      x: Math.max(pad, Math.floor((window.innerWidth - w) / 2)),
      y: Math.max(pad, Math.floor((window.innerHeight - h) / 2)),
    };
  });

  const getMaxRect = useCallback(() => {
    const pad = 12;
    return {
      x: pad,
      y: pad,
      width: Math.max(600, window.innerWidth - pad * 2),
      height: Math.max(400, window.innerHeight - pad * 2),
    };
  }, []);

  const toggleMaximize = useCallback(() => {
    setWindowMode((prev) => (prev === 'max' ? 'norm' : 'max'));
  }, []);

  const onMinimizeClick = useCallback(() => {
    if (windowMode === 'max') setWindowMode('norm');
  }, [windowMode]);

  // Single source of truth for the contact-style card used in profile section
  // (team-grouped) AND teams section (search results pivot). When the user
  // searches, we want the same visual card — captain styling, ratings,
  // FA/FS popup overlay, exact same click handler. No separate "search
  // result" simplified card.
  const renderProfileMemberCard = (member, captainUId = null) => {
    const memberPhoto = member.u_profile_picture || '';
    const memberName = [member.u_first_name, member.u_last_name].filter(Boolean).join(' ') || 'Unknown';
    const memberIndustry = member.b_business_industry || '';
    const isSelected = selectedMember?.u_id === member.u_id;
    const isCaptain = captainUId != null && Number(member.u_id) === Number(captainUId);
    const isPopupOpen = cardPopupMember?.u_id === member.u_id;
    const platformLabel = activePlatform === 'twitter' ? 'X' : activePlatform ? activePlatform.charAt(0).toUpperCase() + activePlatform.slice(1) : '';

    const profileCooldown = activePlatform && activePlatform !== 'email' && cooldowns.profiles[member.u_id];
    const isOptedOut = optedOutUsers.has(Number(member.u_id));
    const isSelf = Number(member.u_id) === Number(profile?.u_id);
    const isBlocked = !!(profileCooldown || isOptedOut || isSelf);
    const blockedReason = isOptedOut
      ? `${memberName} has paused sharing. They've temporarily opted out of being shared on social platforms. They'll be available again once they re-enable sharing.`
      : isSelf
      ? `You can't share your own profile. Ask a teammate to share it for you — that's how eHandshake works!`
      : profileCooldown
      ? `You already shared ${memberName}'s profile on ${platformLabel} recently. To prevent spam and protect your social media accounts, each profile can only be shared once per platform every 30 days. You can share again in ${profileCooldown.days_remaining} day${profileCooldown.days_remaining === 1 ? '' : 's'}.`
      : '';

    const isCooldownPopupOpen = cooldownPopup?.kind === 'profile' && Number(cooldownPopup.id) === Number(member.u_id);
    const profileCooldownUrl = profileCooldown?.platform_post_url || null;
    const isVisuallyBlocked = isBlocked && activePlatform && activePlatform !== 'email';
    // Compact popup text — the full anti-spam explanation lives in the
    // hover tooltip; the popup needs to fit a 130×170px card.
    const shortBlockedReason = isOptedOut
      ? `${memberName} has paused sharing.`
      : isSelf
      ? `Can't share your own profile.`
      : profileCooldown
      ? `Already shared on ${platformLabel}. Try again in ${profileCooldown.days_remaining} day${profileCooldown.days_remaining === 1 ? '' : 's'}.`
      : '';

    return (
      <div
        key={member.u_id}
        onClick={(e) => {
          e.stopPropagation();
          if (isBlocked && activePlatform && activePlatform !== 'email') {
            // Instead of swallowing the click, open a popup with the
            // cooldown reason + a link to the existing live post when
            // available — engaging the live post is what we actually
            // want them to do here.
            setCooldownPopup({ kind: 'profile', id: member.u_id });
            return;
          }
          setHoveredMember(null);
          if (isFreeAgent) {
            setCardPopupMember(isPopupOpen ? null : member);
          } else {
            setShareModalMember(member);
          }
        }}
        onMouseEnter={(e) => onCardMouseEnter(member, e)}
        onMouseLeave={onCardMouseLeave}
        title={isVisuallyBlocked ? blockedReason : ''}
        className={`relative rounded-lg p-2 flex flex-col items-center justify-between text-center text-xs text-gray-700 bg-white ${isVisuallyBlocked && !isCooldownPopupOpen ? 'opacity-40' : 'hover:shadow-lg'} cursor-pointer transition-shadow ${isSelected ? 'ring-2 ring-[#00aef7]' : ''}`}
        style={{
          border: isCaptain ? '2px solid #D4A017' : '1px solid rgba(0, 174, 247, 0.25)',
          borderBottomWidth: isCaptain ? '3px' : '2px',
          boxShadow: isCaptain
            ? '0 2px 6px -1px rgba(212, 160, 23, 0.3), inset 0 -6px 8px -6px rgba(212, 160, 23, 0.2)'
            : '0 2px 4px -1px rgba(0, 174, 247, 0.15), inset 0 -6px 8px -6px rgba(0, 174, 247, 0.12)',
        }}
      >
        <div className={`w-16 h-16 rounded-full overflow-hidden bg-gray-100 mb-2 ${isCaptain ? 'ring-2 ring-[#D4A017] ring-offset-1' : 'border border-[#00aef7]/30'}`}>
          {memberPhoto ? (
            <img
              src={`${process.env.REACT_APP_API_URL}${memberPhoto}?t=${IMG_CACHE_KEY}`}
              alt=""
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-gray-400 text-[10px]">
              No Image
            </div>
          )}
        </div>
        <div className="space-y-0.5 w-full">
          <div className="font-semibold truncate">{memberName}</div>
          <div className="text-[11px] text-gray-500 truncate">{memberIndustry}</div>
          {(() => {
            const avg = Number(member.rating_avg || member.u_avg_rating || 0);
            const cnt = Number(member.rating_count || member.u_review_count || 0);
            if (!avg || avg <= 0) return null;
            const full = Math.floor(avg);
            const half = avg - full >= 0.25;
            const empty = 5 - full - (half ? 1 : 0);
            return (
              <div className="flex flex-col items-center mt-0.5" style={{ fontSize: 10 }}>
                <div className="flex items-center gap-px">
                  {Array.from({ length: full }).map((_, i) => <span key={`f${i}`} className="text-amber-400">&#9733;</span>)}
                  {half && <span className="text-amber-400">&#9733;</span>}
                  {Array.from({ length: empty }).map((_, i) => <span key={`e${i}`} className="text-gray-300">&#9733;</span>)}
                </div>
                <div className="text-[9px] font-bold text-gray-700">{avg.toFixed(2)}</div>
                {cnt > 0 && <div className="text-[9px] text-gray-400">{cnt} review{cnt === 1 ? '' : 's'}</div>}
              </div>
            );
          })()}
        </div>

        {/* FA/FS card popup: Share or View Profile */}
        {isPopupOpen && (
          <div
            className="absolute inset-0 z-10 rounded-lg bg-white/95 backdrop-blur-sm flex flex-col items-center justify-center gap-2 p-2"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => {
                setCardPopupMember(null);
                setShareModalMember(member);
              }}
              disabled={!activePlatform}
              className={`w-full px-3 py-1.5 text-xs font-semibold rounded-md text-white transition-colors ${
                activePlatform ? 'bg-[#00aef7] hover:bg-[#0097d6]' : 'bg-gray-300 cursor-not-allowed'
              }`}
            >
              {activePlatform ? `Share to ${platformLabel}` : 'Select platform first'}
            </button>
            <button
              onClick={() => {
                setCardPopupMember(null);
                openCandidate({
                  candidate: member,
                  viewerUId: profile?.u_id,
                  top: 90,
                  right: 30,
                });
              }}
              className="w-full px-3 py-1.5 text-xs font-semibold rounded-md border border-[#00aef7] text-[#00aef7] hover:bg-[#00aef7]/10 transition-colors"
            >
              View Profile
            </button>
            <button
              onClick={() => setCardPopupMember(null)}
              className="text-[10px] text-gray-400 hover:text-gray-600 mt-0.5"
            >
              Cancel
            </button>
          </div>
        )}

        {/* Cooldown popup. Sized to fit the card it sits on; uses the
            compact `shortBlockedReason` so text doesn't overflow.
            Background is solid white so it visually replaces the dimmed
            card underneath. */}
        {isCooldownPopupOpen && (
          <div
            className="absolute inset-0 z-20 rounded-lg bg-white flex flex-col items-center justify-center gap-1.5 p-2 shadow-lg ring-1 ring-[#00aef7]/40"
            onClick={(e) => e.stopPropagation()}
          >
            <p className="text-[11px] text-gray-800 text-center leading-snug font-medium">
              {shortBlockedReason}
            </p>
            {profileCooldownUrl && (
              <a
                href={profileCooldownUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setCooldownPopup(null)}
                className="w-full px-2 py-1 text-[11px] font-semibold rounded-md text-white bg-[#00aef7] hover:bg-[#0097d6] text-center"
              >
                View the post &rarr;
              </a>
            )}
            <button
              onClick={() => setCooldownPopup(null)}
              className="text-[10px] text-gray-500 hover:text-gray-700"
            >
              Close
            </button>
          </div>
        )}
      </div>
    );
  };

  // Same idea for the company section's card. Extracted so the search
  // (flat deduped grid) and no-search (team-grouped) renders use the
  // identical JSX — same logo, same click handler, same cooldown logic.
  const renderCompanyMemberCard = (member) => {
    const companyLogo = member.b_logo || '';
    const companyName = member.b_name || '';
    const companyIndustry = member.b_business_industry || '';
    const memberName = [member.u_first_name, member.u_last_name].filter(Boolean).join(' ') || 'Unknown';
    const platformLabel = activePlatform === 'twitter' ? 'X' : activePlatform ? activePlatform.charAt(0).toUpperCase() + activePlatform.slice(1) : '';

    const companyCooldown = activePlatform && activePlatform !== 'email' && cooldowns.companies[member.u_id];
    const isOptedOut = optedOutUsers.has(Number(member.u_id));
    const isBlocked = !!(companyCooldown || isOptedOut);
    const blockedReason = isOptedOut
      ? `${memberName} has paused sharing. They've temporarily opted out of being shared on social platforms.`
      : companyCooldown
      ? `You already shared ${companyName || memberName + "'s company"} on ${platformLabel} recently. Each company can only be shared once per platform every 30 days. You can share again in ${companyCooldown.days_remaining} day${companyCooldown.days_remaining === 1 ? '' : 's'}.`
      : '';
    const isCooldownPopupOpen = cooldownPopup?.kind === 'company' && Number(cooldownPopup.id) === Number(member.u_id);
    const companyCooldownUrl = companyCooldown?.platform_post_url || null;
    const isVisuallyBlocked = isBlocked && activePlatform && activePlatform !== 'email';
    const shortBlockedReason = isOptedOut
      ? `${memberName} has paused sharing.`
      : companyCooldown
      ? `Already shared on ${platformLabel}. Try again in ${companyCooldown.days_remaining} day${companyCooldown.days_remaining === 1 ? '' : 's'}.`
      : '';

    return (
      <div
        key={member.u_id}
        onClick={() => {
          if (isBlocked && activePlatform && activePlatform !== 'email') {
            setCooldownPopup({ kind: 'company', id: member.u_id });
            return;
          }
          setCompanyShareModalMember(member);
          setHoveredMember(null);
        }}
        onMouseEnter={(e) => onCardMouseEnter(member, e)}
        onMouseLeave={onCardMouseLeave}
        title={isVisuallyBlocked ? blockedReason : ''}
        className={`relative rounded-lg p-2 flex flex-col items-center justify-between text-center text-xs text-gray-700 bg-white ${isVisuallyBlocked && !isCooldownPopupOpen ? 'opacity-40' : 'hover:shadow-lg'} cursor-pointer transition-shadow`}
        style={{
          border: '1px solid rgba(0, 174, 247, 0.25)',
          borderBottomWidth: '2px',
          boxShadow: '0 2px 4px -1px rgba(0, 174, 247, 0.15), inset 0 -6px 8px -6px rgba(0, 174, 247, 0.12)',
        }}
      >
        <div className="w-16 h-16 rounded-xl overflow-hidden border border-[#00aef7]/20 bg-white mb-2 flex items-center justify-center">
          {companyLogo ? (
            <img
              src={`${process.env.REACT_APP_API_URL}${companyLogo}?t=${IMG_CACHE_KEY}`}
              alt=""
              className="w-full h-full object-contain p-1"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-gray-400 text-[10px]">
              No Logo
            </div>
          )}
        </div>
        <div className="space-y-0.5 w-full">
          <div className="font-semibold truncate">{companyName || 'Company'}</div>
          <div className="text-[11px] text-gray-500 truncate">{companyIndustry}</div>
          {(() => {
            const avg = Number(member.rating_avg || member.u_avg_rating || 0);
            const cnt = Number(member.rating_count || member.u_review_count || 0);
            if (!avg || avg <= 0) return null;
            const full = Math.floor(avg);
            const half = avg - full >= 0.25;
            const empty = 5 - full - (half ? 1 : 0);
            return (
              <div className="flex flex-col items-center mt-0.5" style={{ fontSize: 10 }}>
                <div className="flex items-center gap-px">
                  {Array.from({ length: full }).map((_, i) => <span key={`f${i}`} className="text-amber-400">&#9733;</span>)}
                  {half && <span className="text-amber-400">&#9733;</span>}
                  {Array.from({ length: empty }).map((_, i) => <span key={`e${i}`} className="text-gray-300">&#9733;</span>)}
                </div>
                {cnt > 0 && <div className="text-[9px] text-gray-400">{cnt} review{cnt === 1 ? '' : 's'}</div>}
              </div>
            );
          })()}
        </div>

        {/* Cooldown popup — same pattern as the profile card. */}
        {isCooldownPopupOpen && (
          <div
            className="absolute inset-0 z-20 rounded-lg bg-white flex flex-col items-center justify-center gap-1.5 p-2 shadow-lg ring-1 ring-[#00aef7]/40"
            onClick={(e) => e.stopPropagation()}
          >
            <p className="text-[11px] text-gray-800 text-center leading-snug font-medium">
              {shortBlockedReason}
            </p>
            {companyCooldownUrl && (
              <a
                href={companyCooldownUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setCooldownPopup(null)}
                className="w-full px-2 py-1 text-[11px] font-semibold rounded-md text-white bg-[#00aef7] hover:bg-[#0097d6] text-center"
              >
                View the post &rarr;
              </a>
            )}
            <button
              onClick={() => setCooldownPopup(null)}
              className="text-[10px] text-gray-500 hover:text-gray-700"
            >
              Close
            </button>
          </div>
        )}
      </div>
    );
  };

  if (!sharingCenterOpen) return null;

  return (
    <div
      style={{ zIndex: sharingCenterZ }}
      className="fixed inset-0 flex items-center justify-center pointer-events-none"
      aria-modal="true"
      role="dialog"
    >
      {/* ── Success toast (appears after posting) ── */}
      {postSuccess && (
        <div
          className="pointer-events-auto fixed top-6 left-1/2 -translate-x-1/2 flex items-center gap-2 px-5 py-3 bg-green-50 border border-green-300 rounded-lg shadow-lg animate-[fadeIn_0.2s_ease-out]"
          style={{ zIndex: sharingCenterZ + 10 }}
        >
          <span className="text-green-600 text-lg">&#10003;</span>
          <span className="text-sm font-medium text-green-800">{postSuccess}</span>
          <button onClick={() => setPostSuccess('')} className="ml-2 text-green-400 hover:text-green-600 text-lg leading-none">&times;</button>
        </div>
      )}
      {requestSuccess && (
        <div
          className="pointer-events-auto fixed top-6 left-1/2 -translate-x-1/2 flex items-center gap-2 px-5 py-3 bg-amber-50 border border-amber-300 rounded-lg shadow-lg animate-[fadeIn_0.2s_ease-out]"
          style={{ zIndex: sharingCenterZ + 10 }}
        >
          <span className="text-amber-600 text-lg">&#9993;</span>
          <span className="text-sm font-medium text-amber-800">{requestSuccess}</span>
          <button onClick={() => setRequestSuccess('')} className="ml-2 text-amber-400 hover:text-amber-600 text-lg leading-none">&times;</button>
        </div>
      )}
      <Rnd
        cancel=".eh-sharing-nodrag"
        onMouseDownCapture={() => bringToFront('sharingCenter')}
        size={
          windowMode === 'max'
            ? { width: getMaxRect().width, height: getMaxRect().height }
            : { width: windowRect.width, height: windowRect.height }
        }
        position={
          windowMode === 'max'
            ? { x: getMaxRect().x, y: getMaxRect().y }
            : { x: windowRect.x, y: windowRect.y }
        }
        minWidth={600}
        minHeight={400}
        bounds="window"
        dragHandleClassName="eh-sharing-header"
        disableDragging={windowMode === 'max'}
        enableResizing={
          windowMode === 'max'
            ? false
            : {
                bottomRight: true,
                bottomLeft: true,
                topRight: true,
                topLeft: true,
                left: true,
                right: true,
                bottom: true,
                top: true,
              }
        }
        onDragStop={(e, d) => {
          if (windowMode === 'max') return;
          setWindowRect((prev) => ({
            ...(prev || {}),
            x: d.x,
            y: d.y,
          }));
        }}
        onResizeStop={(e, direction, ref, delta, position) => {
          if (windowMode === 'max') return;
          setWindowRect({
            width: ref.offsetWidth,
            height: ref.offsetHeight,
            x: position.x,
            y: position.y,
          });
        }}
        className="rounded-2xl shadow-2xl overflow-hidden flex flex-col bg-white pointer-events-auto"
      >
        <div
          ref={modalRef}
          className="relative w-full h-full flex flex-col overflow-hidden"
        >
          {/* Header - Brand blue background with white text */}
          <div className="shrink-0 flex items-center justify-between px-4 sm:px-6 py-2 border-b border-[#00aef7] bg-[#00aef7] text-white cursor-move eh-sharing-header">
            <div className="flex items-center gap-3 min-w-0">
              <h2 className="text-base sm:text-lg font-semibold tracking-tight">
                eHandshake Sharing Center
              </h2>
            </div>

            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={toggleMaximize}
                className="eh-sharing-nodrag w-7 h-7 flex items-center justify-center rounded hover:bg-white/20 transition-colors"
                title={windowMode === 'max' ? 'Restore' : 'Maximize'}
              >
                {windowMode === 'max' ? <FaWindowRestore size={12} /> : <FaWindowMaximize size={12} />}
              </button>
              <button
                onClick={closeSharingCenter}
                className="eh-sharing-nodrag w-7 h-7 flex items-center justify-center rounded hover:bg-white/20 transition-colors"
                title="Close"
              >
                <FaTimes size={14} />
              </button>
            </div>
          </div>

          {/* Body — full-width header row, then left/right columns below */}
          <div className="flex-1 min-h-0 flex flex-col">

            {/* Single full-width header row — one continuous border line */}
            <div className="shrink-0 flex border-b border-gray-200 bg-white">
              {/* Logo cell (matches left panel width) */}
              <div className="flex items-center justify-center border-r border-gray-200" style={{ width: 72 }} title="eHandshake">
                <svg
                  width="34"
                  height="34"
                  viewBox="0 0 79.408829 86.059586"
                  xmlns="http://www.w3.org/2000/svg"
                  style={{ flexShrink: 0 }}
                >
                  <g transform="translate(-64.506418,-106.16506)">
                    <path
                      fill="#00aef7"
                      d="m 107.48026,106.1651 c -2.94188,0.003 -3.97523,0.0624 -5.75262,0.33434 -9.722608,1.48765 -17.283528,5.15933 -24.166483,11.7352 -7.43158,7.10005 -11.84167,16.37706 -12.886035,27.10636 -0.658256,6.76259 0.631048,14.6239 3.495911,21.31239 3.577636,8.35259 10.172349,15.56692 18.480525,20.21737 5.482236,3.06866 11.307567,4.77606 18.139462,5.31648 1.47727,0.11686 6.4608,-0.0572 8.21759,-0.28681 8.50554,-1.11174 16.36753,-4.62361 22.7092,-10.14357 1.29647,-1.12845 8.14421,-7.94965 8.14421,-8.11268 0,-0.0655 -2.68761,-2.80568 -5.97225,-6.08955 l -5.97172,-5.97069 -3.55328,3.51296 c -3.90676,3.86311 -5.15565,4.93799 -7.31428,6.29471 -1.8401,1.15651 -3.4364,1.89182 -5.58622,2.57348 -2.82535,0.89587 -4.73046,1.18449 -7.83466,1.18649 -3.62155,0.002 -5.96439,-0.43966 -9.423201,-1.77612 -2.201691,-0.85071 -2.175719,-0.68934 -0.397909,-2.49597 0.840968,-0.8546 1.528589,-1.59261 1.528589,-1.64021 0,-0.16572 -11.84727,-11.92382 -12.014254,-11.92382 -0.09254,0 -0.804387,0.63837 -1.581815,1.41904 -0.777431,0.78068 -1.49136,1.41955 -1.58595,1.41955 -0.184676,0 -1.021828,-1.85141 -1.55391,-3.43648 -1.562043,-4.65337 -1.528007,-10.9888 0.08475,-15.82229 1.30782,-3.91961 3.280932,-7.00906 6.547921,-10.25261 3.751511,-3.72457 7.439982,-5.80408 12.270049,-6.91689 5.21781,-1.20215 11.31849,-0.61281 16.02435,1.54719 0.59578,0.27347 1.08313,0.55254 1.08313,0.62064 0,0.0681 -0.90755,1.02973 -2.01693,2.13682 -1.10938,1.1071 -2.01693,2.08079 -2.01693,2.16369 0,0.197 11.68088,11.88093 11.87783,11.88093 0.0828,0 4.04507,-3.89468 8.80515,-8.65529 l 8.65477,-8.6558 -0.57775,-0.64544 c -0.96153,-1.07379 -6.03847,-6.08607 -7.39438,-7.30033 -5.36489,-4.80443 -12.22037,-8.22992 -19.54351,-9.76529 -3.47998,-0.72962 -5.10494,-0.8908 -8.91935,-0.8878 z m 6.38876,25.26667 -0.65423,0.38086 c -0.35991,0.2095 -1.01796,0.52282 -1.46192,0.69659 -0.73308,0.28691 -3.9491,1.63302 -8.09563,3.38791 l -1.58182,0.66921 -4.344434,4.36098 -4.344438,4.36045 -0.08837,1.26969 c -0.04879,0.6985 -0.155743,2.68229 -0.237711,4.408 -0.08197,1.72571 -0.1921,3.88777 -0.24443,4.80488 -0.05232,0.91687 -0.05425,1.77432 -0.0041,1.90531 0.187342,0.48612 1.68631,0.21765 2.609143,-0.46767 0.839146,-0.62318 1.170793,-1.33767 2.490287,-5.36764 l 1.278992,-3.90673 3.397721,-2.16628 c 1.86886,-1.19149 3.80127,-2.40859 4.29431,-2.70474 l 0.89659,-0.53847 h 7.05693 c 6.18259,-3.4e-4 7.132,0.0294 7.66672,0.24339 0.5273,0.21102 0.66244,0.2163 0.99425,0.0388 0.21123,-0.11305 0.48745,-0.36277 0.6134,-0.555 0.22558,-0.34427 0.15071,-0.42806 -5.00589,-5.58468 z m -5.88646,12.28711 -1.50999,0.93379 c -6.03874,3.73419 -6.2976,3.91195 -6.485391,4.44882 -0.09989,0.28555 -0.629417,1.93093 -1.176673,3.65663 -0.547254,1.72571 -1.171425,3.46433 -1.386995,3.86333 -0.512663,0.9489 -1.505728,1.80801 -2.468583,2.1363 -0.801178,0.27317 -2.253353,0.34697 -2.988449,0.15193 l -0.423229,-0.11214 0.02687,-1.30121 c 0.033,-1.60817 0.02786,-1.67535 -0.129708,-1.67535 -0.160297,0 -1.516186,2.02599 -1.516186,2.26549 0,0.0973 1.999892,2.1655 4.444173,4.59611 l 4.444173,4.41885 0.08372,-1.12035 c 0.04603,-0.61632 0.162509,-1.2647 0.258899,-1.44074 0.493234,-0.90072 1.822759,-1.50056 2.975529,-1.34255 0.34652,0.0475 0.66958,0.0227 0.71778,-0.0553 0.0482,-0.078 -0.0564,-0.90014 -0.23202,-1.82677 -0.51056,-2.69374 -0.25798,-3.62765 1.16685,-4.31808 0.99574,-0.48245 2.01515,-0.46203 2.90318,0.0584 l 0.68678,0.40256 0.38086,-0.29973 c 0.98517,-0.77494 2.31033,-0.95195 3.31091,-0.44183 l 0.58549,0.29869 0.45475,-0.44028 c 0.94232,-0.91192 2.29869,-1.14525 3.49695,-0.60152 0.82113,0.37264 0.88582,0.3696 0.94412,-0.0408 0.047,-0.33068 0.0909,-0.33733 2.66289,-0.41083 1.44575,-0.0413 2.73492,-0.14621 2.88406,-0.23461 0.4278,-0.25373 0.71555,-1.27131 0.5395,-1.90893 -0.25673,-0.9298 -0.47768,-0.99425 -3.42718,-0.99425 -1.84343,0 -2.67965,-0.0535 -2.80551,-0.17932 -0.25969,-0.25975 -0.22234,-0.96721 0.0568,-1.07435 0.12998,-0.0499 1.56061,-0.0909 3.17914,-0.0909 3.22576,0 3.40221,-0.0416 3.75274,-0.8878 0.25356,-0.61216 0.0759,-1.44995 -0.40152,-1.8924 -0.38139,-0.3533 -0.41972,-0.35708 -3.55793,-0.35708 -2.6254,0 -3.20375,-0.0388 -3.35638,-0.22273 -0.1017,-0.12221 -0.14534,-0.32372 -0.0977,-0.44803 0.0732,-0.19072 0.52924,-0.22583 2.92334,-0.22583 1.8809,0 2.96843,-0.0604 3.22822,-0.1788 0.86673,-0.39491 1.10299,-1.84968 0.43047,-2.64893 l -0.38654,-0.4594 h -7.09414 z m 10.57765,12.84934 c -0.9484,0 -1.75484,0.0505 -1.79214,0.11214 -0.0605,0.0999 0.0994,0.90196 0.52141,2.61483 l 0.13798,0.56017 h 1.42368 c 1.57886,0 1.89386,-0.10542 2.22416,-0.74414 0.39132,-0.75674 0.2936,-1.45875 -0.28267,-2.03502 l -0.50798,-0.50798 z m -4.27416,0.30024 c -0.70865,0.0351 -1.4515,0.42024 -1.59835,1.00252 -0.1161,0.46033 0.3197,3.33793 0.59479,3.9269 0.096,0.20544 0.40959,0.49375 0.69712,0.64078 0.59845,0.30604 1.22527,0.24551 1.79317,-0.1726 0.56529,-0.41621 0.68321,-0.92139 0.48472,-2.07739 -0.34838,-2.029 -0.52304,-2.62628 -0.86919,-2.97243 -0.264,-0.264 -0.67708,-0.36886 -1.10226,-0.34778 z m -4.4142,0.74879 c -0.35432,-0.0193 -0.73065,0.0784 -1.08779,0.29611 -0.81478,0.49677 -0.84414,0.79734 -0.33435,3.41839 0.61201,3.14661 0.56562,2.98562 0.97203,3.39205 0.27776,0.27776 0.52018,0.3607 1.05472,0.3607 0.59013,0 0.77009,-0.0761 1.20199,-0.50798 0.28918,-0.28918 0.50837,-0.65898 0.5085,-0.85886 1.3e-4,-0.19311 -0.2075,-1.46046 -0.46147,-2.81637 -0.42022,-2.24345 -0.50123,-2.5024 -0.89917,-2.87631 -0.26805,-0.25187 -0.60014,-0.3884 -0.95446,-0.40773 z m -4.82762,0.18087 c -0.77912,0.0652 -1.52342,0.61557 -1.52342,1.41077 0,0.55753 1.29153,7.6113 1.43661,7.84603 0.0495,0.0801 0.30154,0.27815 0.55965,0.43977 0.81638,0.51121 2.08269,0.0802 2.40244,-0.81753 0.21783,-0.61156 -1.17135,-7.97493 -1.58853,-8.42015 -0.33934,-0.36214 -0.81927,-0.498 -1.28675,-0.45889 z m -3.22564,6.54017 c -0.35783,-0.0198 -0.74229,0.0789 -1.10277,0.29869 -0.774746,0.47236 -0.83834,0.84667 -0.47129,2.78794 0.328,1.73478 0.61345,2.33241 1.2423,2.60036 0.41523,0.17693 1.22734,0.0635 1.6831,-0.23513 0.69433,-0.45494 0.73908,-0.81103 0.3545,-2.80706 -0.29498,-1.53102 -0.41606,-1.88574 -0.76326,-2.23294 -0.25381,-0.2538 -0.58475,-0.39204 -0.94258,-0.41186 z"
                    />
                  </g>
                </svg>
              </div>

              {/* Social platform icons */}
              <div className="flex-1 flex items-center gap-6 px-5 py-2">
                {/* Facebook */}
                <button onClick={() => { setActivePlatform('facebook'); setCreationEmailModal(null); }} className="group flex items-center hover:bg-[#00aef7]/10 justify-center rounded-lg p-2 transition-colors" style={activePlatform === 'facebook' ? { backgroundColor: 'rgba(24,119,242,0.15)' } : {}} title="Facebook">
                  <svg className="w-10 h-10" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="11" fill="#1877F2"/>
                    <path d="M16.671 15.469l.547-3.469H13.89V9.938c0-.949.478-1.875 2.012-1.875H17.3V5.125s-1.239-.21-2.422-.21c-2.473 0-4.088 1.497-4.088 4.21V12H7.688v3.469h3.102V24h3.1V15.469h2.781z" fill="#fff"/>
                  </svg>
                </button>

                {/* Instagram */}
                <button onClick={() => { setActivePlatform('instagram'); setCreationEmailModal(null); }} className="group flex items-center justify-center hover:bg-[#00aef7]/10 rounded-lg p-2 transition-colors" style={activePlatform === 'instagram' ? { backgroundColor: 'rgba(228,64,95,0.15)' } : {}} title="Instagram">
                  <svg className="w-10 h-10" viewBox="0 0 24 24" fill="none">
                    <defs>
                      <linearGradient id="ig-grad" x1="0.5" y1="1" x2="0.5" y2="0">
                        <stop offset="0%" stopColor="#FFDC80"/>
                        <stop offset="25%" stopColor="#F77737"/>
                        <stop offset="50%" stopColor="#E4405F"/>
                        <stop offset="75%" stopColor="#C13584"/>
                        <stop offset="100%" stopColor="#833AB4"/>
                      </linearGradient>
                    </defs>
                    <rect width="24" height="24" rx="6" fill="url(#ig-grad)"/>
                    <rect x="4.5" y="4.5" width="15" height="15" rx="4" stroke="#fff" strokeWidth="1.5" fill="none"/>
                    <circle cx="12" cy="12" r="3.5" stroke="#fff" strokeWidth="1.5" fill="none"/>
                    <circle cx="17" cy="7" r="1.2" fill="#fff"/>
                  </svg>
                </button>

                {/* X / Twitter */}
                <button onClick={() => { setActivePlatform('twitter'); setCreationEmailModal(null); }} className="group flex items-center justify-center hover:bg-[#00aef7]/10 rounded-lg p-2 transition-colors" style={activePlatform === 'twitter' ? { backgroundColor: 'rgba(0,0,0,0.15)' } : {}} title="X / Twitter">
                  <svg className="w-10 h-10" viewBox="0 0 24 24" fill="none">
                    <rect width="24" height="24" rx="4" fill="#000"/>
                    <path d="M16.99 4.5h2.634l-5.752 6.574L20.5 19.5h-5.3l-4.15-5.426L6.46 19.5H3.824l6.153-7.032L3.5 4.5h5.436l3.752 4.962L16.99 4.5zm-.924 13.5h1.46L8.014 5.996H6.44L16.066 18z" fill="#fff"/>
                  </svg>
                </button>

                {/* YouTube */}
                <button
                  onClick={() => { setActivePlatform('youtube'); setCreationEmailModal(null); }}
                  className="group flex items-center justify-center hover:bg-[#00aef7]/10 rounded-lg p-2 transition-colors"
                  style={activePlatform === 'youtube' ? { backgroundColor: 'rgba(255,0,0,0.15)' } : {}}
                  title="YouTube"
                >
                  <svg className="w-10 h-10" viewBox="0 0 24 24" fill="none">
                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" fill="#FF0000"/>
                  </svg>
                </button>

                {/* LinkedIn — disabled for profile/company/team sections (personal pages only) */}
                <button
                  onClick={() => { if (activeSection !== 'creations') return; setActivePlatform('linkedin'); setCreationEmailModal(null); }}
                  className={`group flex items-center justify-center rounded-lg p-2 transition-colors ${activeSection !== 'creations' ? 'opacity-30 cursor-not-allowed' : 'hover:bg-[#00aef7]/10'}`}
                  style={activePlatform === 'linkedin' ? { backgroundColor: 'rgba(10,102,194,0.15)' } : {}}
                  title={activeSection !== 'creations' ? 'LinkedIn is only available for Design Center creations' : 'LinkedIn'}
                >
                  <svg className="w-10 h-10" viewBox="0 0 24 24" fill="none">
                    <rect width="24" height="24" rx="4" fill="#0A66C2"/>
                    <path d="M8.339 18.338H5.667V9.75h2.672v8.588zM7.003 8.574a1.548 1.548 0 110-3.096 1.548 1.548 0 010 3.096zM18.338 18.338h-2.669v-4.177c0-.996-.018-2.278-1.388-2.278-1.39 0-1.602 1.086-1.602 2.206v4.249h-2.669V9.75h2.564v1.172h.035c.357-.675 1.228-1.388 2.528-1.388 2.703 0 3.201 1.779 3.201 4.092v4.712z" fill="#fff"/>
                  </svg>
                </button>

                {/* TikTok */}
                <button onClick={() => { setActivePlatform('tiktok'); setCreationEmailModal(null); }} className="group flex items-center justify-center hover:bg-[#00aef7]/10 rounded-lg p-2 transition-colors" style={activePlatform === 'tiktok' ? { backgroundColor: 'rgba(0,0,0,0.15)' } : {}} title="TikTok">
                  <svg className="w-10 h-10" viewBox="0 0 24 24" fill="none">
                    <rect width="24" height="24" rx="4" fill="#000"/>
                    <path d="M16.6 5.82s.51.49 1.86.49v2.64c-.7 0-1.37-.14-2-.42v4.62c0 3.71-4.04 5.22-6.15 3.21-1.36-1.29-1.48-3.47-.25-4.94 1.28-1.53 3.57-1.58 4.14-1.56v2.7c-.28-.04-.6-.03-.93.08-.87.3-1.37 1.09-1.2 2.04.21 1.16 1.44 1.73 2.47 1.16.64-.35.92-1 .92-1.79V5h2.28c.13 1.26.86 2.35 1.86 2.82z" fill="#25F4EE"/>
                    <path d="M16.6 5.82s.51.49 1.86.49v2.64c-.7 0-1.37-.14-2-.42v4.62c0 3.71-4.04 5.22-6.15 3.21-1.36-1.29-1.48-3.47-.25-4.94 1.28-1.53 3.57-1.58 4.14-1.56v2.7c-.28-.04-.6-.03-.93.08-.87.3-1.37 1.09-1.2 2.04.21 1.16 1.44 1.73 2.47 1.16.64-.35.92-1 .92-1.79V5h2.28" fill="#FE2C55" opacity="0.8"/>
                    <path d="M12.2 9.65v4.62c0 3.71-4.04 5.22-6.15 3.21-.56-.53-.92-1.21-1.09-1.94 1.03.76 2.5.61 3.38-.37.64-.72.76-1.73.39-2.58v-6.6h2.28c0 .21.03.42.07.62.16.96.63 1.82 1.32 2.45-.08-.15-.15-.3-.2-.46v.05z" fill="#000" opacity="0.2"/>
                  </svg>
                </button>

                {/* Email */}
                <button onClick={() => setActivePlatform('email')} className="group flex items-center justify-center hover:bg-[#00aef7]/10 rounded-lg p-2 transition-colors" style={activePlatform === 'email' ? { backgroundColor: 'rgba(0,174,247,0.15)' } : {}} title="Email">
                  <svg className="w-10 h-10" xmlns="http://www.w3.org/2000/svg" viewBox="0 -960 960 960" fill="#00aef7">
                    <path d="M280-280q-33 0-56.5-23.5T200-360v-400q0-33 23.5-56.5T280-840h560q33 0 56.5 23.5T920-760v400q0 33-23.5 56.5T840-280H280Zm280-188L280-663v303h560v-303L560-468Zm0-98 280-194H280l280 194ZM120-120q-33 0-56.5-23.5T40-200v-500h80v500h660v80H120Zm720-546v-94H280v94-94h560v94Z"/>
                  </svg>
                </button>
              </div>{/* end social icons cell */}
            </div>{/* end full-width header row */}

            {/* Two-column content area below the header */}
            <div className="flex-1 min-h-0 flex">
              {/* Left Panel - icon sidebar */}
              <div className="eh-sharing-nodrag shrink-0 border-r border-gray-200 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent flex flex-col items-center gap-6 pt-5 pb-4" style={{ width: 72 }}>

                {/* Creations */}
                <button onClick={() => setActiveSection(activeSection === 'creations' ? null : 'creations')} className="flex items-center justify-center hover:bg-[#00aef7]/10 rounded-lg transition-colors p-1" style={activeSection === 'creations' ? { backgroundColor: 'rgba(0,174,247,0.15)' } : {}} title="Design Center Creations">
                  <svg xmlns="http://www.w3.org/2000/svg" height="48px" viewBox="0 -960 960 960" width="48px" fill="#00aef7"><path d="M240-120q-45 0-89-22t-71-58q26 0 53-20.5t27-59.5q0-50 35-85t85-35q50 0 85 35t35 85q0 66-47 113t-113 47Zm0-80q33 0 56.5-23.5T320-280q0-17-11.5-28.5T280-320q-17 0-28.5 11.5T240-280q0 23-5.5 42T220-202q5 2 10 2h10Zm230-160L360-470l358-358q11-11 27.5-11.5T774-828l54 54q12 12 12 28t-12 28L470-360Zm-190 80Z"/></svg>
                </button>

                {/* Profile */}
                <button
                  onClick={() => setActiveSection('profile')}
                  className="flex items-center justify-center rounded-lg transition-colors p-1 hover:bg-[#00aef7]/10"
                  style={activeSection === 'profile' ? { backgroundColor: 'rgba(0,174,247,0.15)' } : {}}
                  title="Teammate Profile"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 -960 960 960" fill="#00aef7">
                    <path d="M480-480q-66 0-113-47t-47-113q0-66 47-113t113-47q66 0 113 47t47 113q0 66-47 113t-113 47ZM160-160v-112q0-34 17.5-62.5T224-378q62-31 126-46.5T480-440q66 0 130 15.5T736-378q29 15 46.5 43.5T800-272v112H160Zm80-80h480v-32q0-11-5.5-20T700-306q-54-27-109-40.5T480-360q-56 0-111 13.5T260-306q-9 5-14.5 14t-5.5 20v32Zm240-320q33 0 56.5-23.5T560-640q0-33-23.5-56.5T480-720q-33 0-56.5 23.5T400-640q0 33 23.5 56.5T480-560Zm0-80Zm0 400Z"/>
                  </svg>
                </button>

                {/* Company */}
                <button
                  onClick={() => setActiveSection('company')}
                  className="flex items-center justify-center rounded-lg transition-colors p-1 hover:bg-[#00aef7]/10"
                  style={activeSection === 'company' ? { backgroundColor: 'rgba(0,174,247,0.15)' } : {}}
                  title="Teammate Company Profile"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 -960 960 960" fill="#00aef7">
                    <path d="M80-120v-720h400v160h400v560H80Zm80-80h240v-80H160v80Zm0-160h240v-80H160v80Zm0-160h240v-80H160v80Zm0-160h240v-80H160v80Zm320 480h320v-400H480v400Zm80-240v-80h160v80H560Zm0 160v-80h160v80H560Z"/>
                  </svg>
                </button>

                {/* Teams */}
                <button
                  onClick={() => setActiveSection('teams')}
                  className="flex items-center justify-center rounded-lg transition-colors p-1 hover:bg-[#00aef7]/10"
                  style={activeSection === 'teams' ? { backgroundColor: 'rgba(0,174,247,0.15)' } : {}}
                  title="Team Profiles"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 -960 960 960" fill="#00aef7">
                    <g transform="translate(0 180)">
                      <path d="m 438.32296,-640 q -66,0 -113,-47 -47,-47 -47,-113 0,-66 47,-113 47,-47 113,-47 66,0 113,47 47,47 47,113 0,66 -47,113 -47,47 -113,47 z m -320,320 v -112 q 0,-34 17.5,-62.5 17.5,-28.5 46.5,-43.5 62,-31 126,-46.5 64,-15.5 130,-15.5 66,0 130,15.5 64,15.5 126,46.5 29,15 46.5,43.5 17.5,28.5 17.5,62.5 v 112 z m 80,-80 h 480 v -32 q 0,-11 -5.5,-20 -5.5,-9 -14.5,-14 -54,-27 -109,-40.5 -55,-13.5 -111,-13.5 -56,0 -111,13.5 -55,13.5 -109,40.5 -9,5 -14.5,14 -5.5,9 -5.5,20 z m 240,-320 q 33,0 56.5,-23.5 23.5,-23.5 23.5,-56.5 0,-33 -23.5,-56.5 -23.5,-23.5 -56.5,-23.5 -33,0 -56.5,23.5 -23.5,23.5 -23.5,56.5 0,33 23.5,56.5 23.5,23.5 56.5,23.5 z"/>
                      <path d="m 148.49481,-748.60774 c -20.41779,0 -37.88275,7.93264 -52.422749,23.86194 -14.53984,15.92932 -21.82466,35.70122 -21.82466,59.24903 0,22.85521 7.28482,42.40514 21.82466,58.68089 14.539999,16.2756 32.004959,24.43007 52.422749,24.43007 21.0365,0 38.69978,-8.15447 52.93027,-24.43007 14.23051,-16.27575 21.31712,-35.82568 21.31712,-58.68089 0,-23.54781 -7.08661,-43.31971 -21.31712,-59.24903 -14.23049,-15.9293 -31.89377,-23.86194 -52.93027,-23.86194 z m 593.97923,0 c -20.41775,0 -37.88269,7.93264 -52.42276,23.86194 -14.53987,15.92932 -21.82464,35.70122 -21.82464,59.24903 0,22.85521 7.28477,42.40514 21.82464,58.68089 14.54007,16.2756 32.00501,24.43007 52.42276,24.43007 21.03653,0 38.6998,-8.15447 52.93033,-24.43007 14.23045,-16.27575 21.31709,-35.82568 21.31709,-58.68089 0,-23.54781 -7.08664,-43.31971 -21.31709,-59.24903 -14.23053,-15.9293 -31.8938,-23.86194 -52.93033,-23.86194 z m -593.97923,207.7774 c -44.54781,0 -80.449569,9.38274 -107.673245,28.08242 C 13.5979,-494.04826 0,-469.80685 0,-440.02584 v 65.41741 h 167.05665 v -67.52766 c 0,-17.31448 2.19456,-33.90478 6.52566,-49.83411 4.33107,-15.92931 10.76986,-31.23168 19.43193,-45.77595 -6.80589,-1.38518 -13.89252,-2.25092 -21.31714,-2.59723 -7.42459,-0.34628 -15.15895,-0.48696 -23.20229,-0.48696 z m 593.97923,0 c -7.42463,0 -14.70942,0.14068 -21.82466,0.48696 -7.11539,0.34631 -14.07619,1.21205 -20.88207,2.59723 8.04331,13.85168 14.10515,28.79153 18.12677,44.72084 4.02179,15.92944 6.0181,32.88213 6.0181,50.88922 v 67.52766 h 167.05665 v -65.41741 c 0,-30.47358 -13.59773,-54.93684 -40.82155,-73.29022 -27.22365,-18.35353 -63.12544,-27.51428 -107.67324,-27.51428 z"/>
                    </g>
                  </svg>
                </button>

              </div>{/* end left icon sidebar */}
{/* Can you see this? */}
              {/* Right Panel - main content area */}
              <div className="eh-sharing-nodrag flex-1 min-w-0 overflow-hidden bg-[#f8fafc] flex flex-col mb-[3px] scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent">

                {activeSection === 'creations' && (<>
                {/* ─── INLINE POST VIEW (landing page rendered inside Sharing Center) ─── */}
                {viewingPost && !sharingCenterPending ? (
                  <div className="flex-1 flex flex-col min-h-0">
                    {/* Toolbar: back arrow + title + Post button */}
                    <div className="shrink-0 flex items-center justify-between px-4 py-2 border-b border-gray-200 bg-white">
                      <div className="flex items-center gap-3 min-w-0">
                        <button
                          onClick={closePostView}
                          className="flex items-center gap-1 text-sm text-gray-600 hover:text-[#00aef7] transition-colors"
                          title="Back to posts"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
                          </svg>
                          <span className="font-medium">Back</span>
                        </button>
                        <span className="text-gray-300">|</span>
                        <span className="text-sm font-semibold text-gray-800 truncate">{viewingPost.headline || '(untitled)'}</span>
                      </div>
                      {(() => {
                        const isSocial = SOCIAL_PLATFORMS.includes(activePlatform);
                        const canPost = !!activePlatform;
                        const isOwnPost = viewingPost && Number(viewingPost.post_u_id) === Number(profile?.u_id);
                        const freeAgentSelfBlock = false;
                        // Does THIS creation already have a publish on the active platform?
                        const publishForPlatform = activePlatform && viewingPost?.publishes?.[activePlatform];
                        const isAlreadyPosted = !!publishForPlatform && activePlatform !== 'email';
                        const platformLabel = activePlatform === 'twitter' ? 'X' : (activePlatform ? activePlatform.charAt(0).toUpperCase() + activePlatform.slice(1) : '');

                        // VIEW button — when this creation is already posted to this platform and has a URL
                        if (isAlreadyPosted && publishForPlatform.url) {
                          const handleView = async () => {
                            // Track the click server-side
                            if (publishForPlatform.publish_id) {
                              // New-system post: simple click record
                              try {
                                fetch(`${API_URL}/api/sharing/track-click`, {
                                  method: 'POST',
                                  credentials: 'include',
                                  headers: { 'Content-Type': 'application/json' },
                                  body: JSON.stringify({ publish_id: publishForPlatform.publish_id }),
                                });
                              } catch {}
                            } else {
                              // Legacy post: create social_publishes row on-the-fly + record click
                              try {
                                const resp = await fetch(`${API_URL}/api/sharing/track-click-legacy`, {
                                  method: 'POST',
                                  credentials: 'include',
                                  headers: { 'Content-Type': 'application/json' },
                                  body: JSON.stringify({
                                    post_id: viewingPost.post_id,
                                    platform: activePlatform,
                                    url: publishForPlatform.url,
                                  }),
                                });
                                const data = await resp.json();
                                // Update local publish_id so subsequent clicks use the fast path
                                if (data.publish_id) {
                                  setPosts(prev => prev.map(p => {
                                    if (p.post_id !== viewingPost.post_id) return p;
                                    const up = { ...p.publishes };
                                    if (up[activePlatform]) {
                                      up[activePlatform] = { ...up[activePlatform], publish_id: data.publish_id };
                                    }
                                    return { ...p, publishes: up };
                                  }));
                                }
                              } catch {}
                            }
                            // ALWAYS mark as clicked in local state with current timestamp
                            setPosts(prev => prev.map(p => {
                              if (p.post_id !== viewingPost.post_id) return p;
                              const updatedPublishes = { ...p.publishes };
                              if (updatedPublishes[activePlatform]) {
                                updatedPublishes[activePlatform] = {
                                  ...updatedPublishes[activePlatform],
                                  viewer_clicked: true,
                                  viewer_clicked_at: new Date().toISOString(),
                                };
                              }
                              return { ...p, publishes: updatedPublishes };
                            }));
                            window.open(publishForPlatform.url, '_blank', 'noopener,noreferrer');
                          };
                          const likeCount = publishForPlatform.like_count || 0;
                          const commentCount = publishForPlatform.comment_count || 0;
                          const viewCount = publishForPlatform.view_count || 0;
                          const hasEngagement = likeCount > 0 || commentCount > 0 || viewCount > 0;
                          return (
                            <div className="flex flex-col items-end gap-1">
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={handleView}
                                  className="px-5 py-1.5 text-white text-sm font-semibold rounded-lg transition-colors whitespace-nowrap bg-green-600 hover:bg-green-700"
                                  title={`View this post on ${platformLabel}`}
                                >
                                  View on {platformLabel}
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigator.clipboard.writeText(publishForPlatform.url).then(() => {
                                      setCopyLinkFeedback(viewingPost.post_id);
                                      setTimeout(() => setCopyLinkFeedback(null), 2000);
                                    });
                                  }}
                                  className="p-1.5 rounded-lg border border-gray-300 hover:bg-gray-100 transition-colors"
                                  title="Copy link"
                                >
                                  {copyLinkFeedback === viewingPost.post_id ? (
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 text-green-500">
                                      <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z" clipRule="evenodd" />
                                    </svg>
                                  ) : (
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 text-gray-500">
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 0 1 1.242 7.244l-4.5 4.5a4.5 4.5 0 0 1-6.364-6.364l1.757-1.757m13.35-.622 1.757-1.757a4.5 4.5 0 0 0-6.364-6.364l-4.5 4.5a4.5 4.5 0 0 0 1.242 7.244" />
                                    </svg>
                                  )}
                                </button>
                              </div>
                              {hasEngagement && (
                                <div className="flex items-center gap-2 text-[11px] text-gray-600">
                                  {likeCount > 0 && (
                                    <span title={`${likeCount} likes`} className="inline-flex items-center gap-0.5">
                                      <span className="text-red-500">❤</span> {likeCount.toLocaleString()}
                                    </span>
                                  )}
                                  {commentCount > 0 && (
                                    <span title={`${commentCount} comments`} className="inline-flex items-center gap-0.5">
                                      💬 {commentCount.toLocaleString()}
                                    </span>
                                  )}
                                  {viewCount > 0 && (
                                    <span title={`${viewCount} views`} className="inline-flex items-center gap-0.5">
                                      👁 {viewCount.toLocaleString()}
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        }

                        // POSTED but no URL — legacy post from before URL tracking.
                        // Show a disabled "Posted" badge for non-owners so they don't see a Post button.
                        if (isAlreadyPosted && !publishForPlatform.url && !isOwnPost) {
                          return (
                            <button
                              disabled
                              className="px-5 py-1.5 text-white text-sm font-semibold rounded-lg bg-gray-400 cursor-not-allowed whitespace-nowrap"
                              title="This post was published before link tracking was available"
                            >
                              Posted on {platformLabel}
                            </button>
                          );
                        }

                        // ASK AUTHOR TO PUBLISH — non-owner viewing an un-posted platform slot
                        if (!isOwnPost && !isAlreadyPosted && isSocial && activePlatform !== 'email') {
                          const isRequesting = requestingPublish?.post_id === viewingPost.post_id && requestingPublish?.platform === activePlatform;
                          return (
                            <div className="flex flex-col items-end gap-1">
                              <button
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  setRequestingPublish({ post_id: viewingPost.post_id, platform: activePlatform });
                                  try {
                                    const resp = await fetch(`${API_URL}/api/sharing/request-publish`, {
                                      method: 'POST',
                                      credentials: 'include',
                                      headers: { 'Content-Type': 'application/json' },
                                      body: JSON.stringify({ post_id: viewingPost.post_id, platform: activePlatform }),
                                    });
                                    const data = await resp.json();
                                    if (data.ok) {
                                      setRequestSuccess(data.already_requested
                                        ? 'You already requested this recently.'
                                        : `Request sent! The author will be notified to publish on ${platformLabel}.`);
                                      setTimeout(() => setRequestSuccess(''), 4000);
                                    } else {
                                      setRequestSuccess(data.error || 'Could not send request.');
                                      setTimeout(() => setRequestSuccess(''), 4000);
                                    }
                                  } catch {
                                    setRequestSuccess('Network error. Try again.');
                                    setTimeout(() => setRequestSuccess(''), 4000);
                                  }
                                  setRequestingPublish(null);
                                }}
                                disabled={isRequesting}
                                className="px-5 py-1.5 text-white text-sm font-semibold rounded-lg transition-colors whitespace-nowrap bg-amber-500 hover:bg-amber-600 disabled:opacity-50"
                                title={`Ask the author to publish this on ${platformLabel}`}
                              >
                                {isRequesting ? 'Sending…' : `Ask Author to Publish on ${platformLabel}`}
                              </button>
                              <p className="text-[10px] text-gray-400 max-w-[260px] text-right">
                                The creator hasn't posted this to {platformLabel} yet. Send them a nudge!
                              </p>
                            </div>
                          );
                        }

                        // POST button — when this creation hasn't been posted to this platform yet
                        // (owner sees this to post their own creation)
                        return (
                          <button
                            onClick={
                              freeAgentSelfBlock
                                ? undefined
                                : activePlatform === 'email'
                                  ? () => setCreationEmailModal(viewingPost)
                                  : isSocial
                                    ? () => { setPostError(''); setShowPostModal(true); }
                                    : handleSharePost
                            }
                            disabled={!canPost || shareLoading || freeAgentSelfBlock}
                            className={`px-5 py-1.5 text-white text-sm font-semibold rounded-lg transition-colors whitespace-nowrap ${
                              !canPost || freeAgentSelfBlock
                                ? 'bg-gray-300 cursor-not-allowed'
                                : shareLoading
                                  ? 'bg-[#00aef7]/60 cursor-wait'
                                  : 'bg-[#00aef7] hover:bg-[#0097d6]'
                            }`}
                            title={freeAgentSelfBlock ? '' : !activePlatform ? 'Select a platform first' : `Post to ${platformLabel}`}
                          >
                            {freeAgentSelfBlock
                              ? 'Share others\' designs to post'
                              : shareLoading
                                ? 'Posting…'
                                : activePlatform
                                  ? `Post to ${platformLabel}`
                                  : 'Select Platform'}
                          </button>
                        );
                      })()}
                    </div>

                    {/* Free Agent self-post toast */}
                    {showFreeAgentToast && (
                      <div className="mx-4 mt-2 p-3 bg-amber-50 border border-amber-300 rounded-lg text-sm text-amber-800 relative">
                        <button
                          onClick={() => setShowFreeAgentToast(false)}
                          className="absolute top-1 right-2 text-amber-400 hover:text-amber-600 text-lg leading-none"
                        >&times;</button>
                        <p className="font-semibold mb-1">Free Agents cannot post their own creations</p>
                        <p>
                          eHandshake is a sharing & collaboration platform designed to encourage posting interactivity amongst local, small-business users.
                          You can find another user's creation that you like or that you think your social audience will enjoy, and share it.
                          Your design can be shared by any other Free Agent or First String user in the system — so design it well!
                        </p>
                      </div>
                    )}

                    {/* Detail content area — Platform View on top, Landing Page below */}
                    <div className="flex-1 overflow-y-auto pb-[200px] scrollbar-thin scrollbar-thumb-gray-200 scrollbar-track-transparent">
                      {/* ─── Top section: Platform View ─── */}
                      {activePlatform && activePlatform !== 'email' && activeSection === 'creations' && (() => {
                        const FrameComp = CREATION_FRAME_MAP[activePlatform];
                        if (!FrameComp) return null;

                        const c = viewingPost.captions_json;
                        const rawCaption = c
                          ? (typeof c === 'string' ? c : (c[activePlatform] || c.facebook || c.youtube || c.instagram || Object.values(c)[0] || ''))
                          : '';
                        const pvCreatorName = [viewingPost.u_first_name, viewingPost.u_last_name].filter(Boolean).join(' ');
                        const pvBizName = viewingPost.b_name || '';
                        const pvCaption = [
                          rawCaption,
                          (pvCreatorName || pvBizName) ? `— Created by ${pvCreatorName}${pvBizName ? `, ${pvBizName}` : ''}` : '',
                        ].filter(Boolean).join('\n');

                        // A post has video content if its primary asset is video OR a
                        // tiktok/Ken Burns asset is attached. Both FB Reels and IG Reels
                        // render the same 9:16 frame as TT/YT in that case.
                        const postHasVideo = viewingPost.asset_type === 'video' || !!viewingPost.tiktok_video_asset_id;

                        // The frame is vertical 9:16 when the platform is inherently
                        // vertical (TT/YT) OR when the post is video on a Reels-capable
                        // platform (FB/IG). Critical for sizing the reserved layout slot
                        // — without this, FB's old 480px allocation gets blown out by
                        // the new 533px Reel frame and overflows into the Landing Page
                        // section below.
                        const isVertical = activePlatform === 'youtube' || activePlatform === 'tiktok'
                          || (postHasVideo && (activePlatform === 'facebook' || activePlatform === 'instagram'));
                        const scale = isVertical ? 1.2 : 1.35;
                        // Native widths per platform frame (from PublishPreviewModal.jsx).
                        // Must match so internal text layout doesn't wrap wrong.
                        const NATIVE_WIDTHS = {
                          facebook: 340, instagram: 300, linkedin: 340, twitter: 340,
                          tiktok: 300, youtube: 300,
                        };
                        // Generous heights to accommodate variable caption lengths
                        // so the scaled preview never overflows into the Landing Page section.
                        const NATIVE_HEIGHTS = {
                          facebook: 480, instagram: 560, linkedin: 500, twitter: 420,
                          tiktok: 533, youtube: 533,
                        };
                        // When the frame is a 9:16 Reel, override width/height to match
                        // the actual rendered Reel frame (300×533) regardless of platform.
                        const nativeWidth  = isVertical ? 300 : (NATIVE_WIDTHS[activePlatform]  || 300);
                        const nativeHeight = isVertical ? 533 : (NATIVE_HEIGHTS[activePlatform] || 480);
                        const scaledWidth  = Math.round(nativeWidth  * scale);
                        const scaledHeight = Math.round(nativeHeight * scale);

                        // For TikTok/YouTube and now FB/IG Reels, use the generated Ken
                        // Burns video asset (if it exists) so it plays instead of showing
                        // a static image. "Generated" here means a Ken Burns asset was
                        // actually produced from a still image — the server stores a NEW
                        // asset id distinct from image_asset_id. When the user uploaded
                        // their own video, tiktok_video_asset_id === image_asset_id
                        // (auto-aliased), so this still uses the hero asset but NO burn-in
                        // happened.
                        const hasVideoAsset = isVertical && viewingPost.tiktok_video_asset_id;
                        const kenBurnsGenerated = hasVideoAsset
                          && viewingPost.tiktok_video_asset_id !== viewingPost.image_asset_id;
                        const useVideoAssetId = hasVideoAsset
                          ? viewingPost.tiktok_video_asset_id
                          : viewingPost.image_asset_id;
                        const useAssetType = hasVideoAsset
                          ? 'video'
                          : (viewingPost.asset_type === 'video' ? 'video' : 'image');
                        // Only Ken Burns videos have the headline baked in via FFmpeg — suppress
                        // the React overlay in that case to avoid double-rendering. User-uploaded
                        // videos have no burn-in, so render the overlay on top as normal.
                        const suppressHeadlineOverlay = kenBurnsGenerated;

                        // Per-platform headline position override (set by the user in the
                        // PublishPreviewModal drag-to-reposition UI). Falls back to the global
                        // headlineStyle posX/posY, then to sensible defaults.
                        const platformOverlay = viewingPost.platform_overlays_json?.[activePlatform];
                        const effectiveHeadlinePos = {
                          x: platformOverlay?.posX ?? viewingPost.headline_style_json?.posX ?? 50,
                          y: platformOverlay?.posY ?? viewingPost.headline_style_json?.posY ?? 71,
                        };
                        const effectiveHeadlineStyle = platformOverlay
                          ? { ...(viewingPost.headline_style_json || {}), ...platformOverlay }
                          : (viewingPost.headline_style_json || { fontSize: 14, color: '#FFFFFF', bgOpacity: 0.6, boxWidth: 60 });

                        const platformLabel = activePlatform === 'twitter' ? 'X'
                          : activePlatform.charAt(0).toUpperCase() + activePlatform.slice(1);

                        return (
                          <div className="bg-gray-50 px-6 py-6">
                            <div className="max-w-[900px] mx-auto">
                              <div className="flex items-center justify-between mb-4">
                                <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">
                                  {platformLabel} Preview
                                </h3>
                                <span className="text-[11px] text-gray-400">How this will appear on {platformLabel}</span>
                              </div>
                              <div className="flex justify-center">
                                {/* Outer wrapper reserves the real scaled footprint so layout is accurate */}
                                <div style={{ width: `${scaledWidth}px`, height: `${scaledHeight}px`, position: 'relative' }}>
                                  <div style={{
                                    transform: `scale(${scale})`,
                                    transformOrigin: 'top left',
                                    width: `${nativeWidth}px`,
                                    position: 'absolute',
                                    top: 0, left: 0,
                                    filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.12))'
                                  }}>
                                    <FrameComp
                                      src={assetUrl(useVideoAssetId)}
                                      position={{ x: 50, y: 50 }}
                                      zoom={1}
                                      headline={suppressHeadlineOverlay ? '' : (viewingPost.headline || '')}
                                      headlinePos={effectiveHeadlinePos}
                                      headlineStyle={effectiveHeadlineStyle}
                                      caption={pvCaption}
                                      biz={sharerBiz}
                                      assetType={useAssetType}
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })()}

                      {/* ─── Clear divider between sections ─── */}
                      <div className="h-3 bg-gradient-to-b from-gray-200 to-gray-100" />

                      {/* ─── Bottom section: Landing Page ─── */}
                      <div className="px-6 py-4 border-b border-gray-200 bg-white">
                        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">
                          Landing Page{activePlatform && activePlatform !== 'email'
                            ? ` When Clicked From ${activePlatform === 'twitter' ? 'X' : activePlatform.charAt(0).toUpperCase() + activePlatform.slice(1)}`
                            : ''}
                        </h3>
                      </div>
                      {lpLoading ? (
                        <div className="flex items-center justify-center py-16">
                          <div className="w-8 h-8 border-4 border-[#00aef7] border-t-transparent rounded-full animate-spin" />
                        </div>
                      ) : landingPageData ? (
                        (() => {
                          const lp = landingPageData;
                          const accentColor = lp.accent_color || '#2563EB';
                          const heroImageUrl = assetUrl(lp.hero_image_id);
                          const lpCtaType = lp.cta_type || 'contact_us';
                          const sections = (lp.sections_json || []).map(s => ({
                            ...s,
                            image_url: assetUrl(s.image_asset_id),
                          }));
                          const handleLpCtaClick = () => {
                            if (lpCtaType === 'contact_us') {
                              setShowLpContact(true);
                            } else if (lpCtaType === 'call_now' && lp.cta_url) {
                              window.open(lp.cta_url.startsWith('tel:') ? lp.cta_url : `tel:${lp.cta_url}`, '_self');
                            } else if (lp.cta_url) {
                              window.open(lp.cta_url.startsWith('http') ? lp.cta_url : `https://${lp.cta_url}`, '_blank', 'noopener');
                            }
                          };
                          const lpBizInfo = {
                            name: lp.business_name || '',
                            address: lp.business_address || '',
                            city: lp.business_city || '',
                            state: lp.business_state || '',
                            zip: lp.business_zip || '',
                            phone: lp.business_phone || '',
                            website: lp.business_website || '',
                            email: lp.user_email || '',
                          };
                          const props = {
                            heroImageUrl,
                            heroAssetType: lp.hero_asset_type || 'image',
                            heroHeadline: lp.hero_headline,
                            heroSubheadline: lp.hero_subheadline,
                            sections,
                            accentColor,
                            ctaText: lp.cta_text,
                            onCtaClick: handleLpCtaClick,
                            businessName: lp.business_name,
                          };
                          let lpLayout;
                          switch (lp.layout) {
                            case 'grid-gallery':      lpLayout = <GridGalleryPublic {...props} />; break;
                            case 'feature-strip':     lpLayout = <FeatureStripPublic {...props} />; break;
                            case 'compact-editorial': lpLayout = <CompactEditorialPublic {...props} />; break;
                            default:                  lpLayout = <BoldHeroPublic {...props} />;
                          }
                          return (
                            <>
                              {lpLayout}
                              {showLpContact && (
                                <ContactToast bizInfo={lpBizInfo} accentColor={accentColor} onClose={() => setShowLpContact(false)} />
                              )}
                            </>
                          );
                        })()
                      ) : viewingPost.external_url ? (
                        /* ─── External URL post: OG metadata link preview card ─── */
                        <ExternalUrlPreview url={viewingPost.external_url} authorName={[viewingPost.u_first_name, viewingPost.u_last_name].filter(Boolean).join(' ')} />
                      ) : (
                        <div className="flex items-center justify-center py-16">
                          <p className="text-sm text-gray-400">No landing page data available for this post.</p>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  /* ─── GRID LIST VIEW (default) ─── */
                  <>
                    {/* Throbber overlay while publish is in progress */}
                    {sharingCenterPending && (
                      <div className="flex-1 flex items-center justify-center">
                        <div className="text-center">
                          <div className="w-10 h-10 border-4 border-[#00aef7] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                          <p className="text-sm font-medium text-gray-500">Publishing to Sharing Center...</p>
                        </div>
                      </div>
                    )}

                    {/* Moderation rejection banner */}
                    {sharingCenterModerationMsg && !sharingCenterPending && (
                      <div className="shrink-0 flex items-center gap-3 px-4 py-3 bg-red-50 border-b border-red-200">
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-red-500 shrink-0" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 10-2 0 1 1 0 002 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        <p className="text-sm font-medium text-red-700 flex-1">{sharingCenterModerationMsg}</p>
                        <button
                          onClick={() => setSharingCenterModerationMsg(null)}
                          className="text-red-400 hover:text-red-600 text-lg leading-none px-1"
                          title="Dismiss"
                        >✕</button>
                      </div>
                    )}

                    {/* Upgrade banner — shown after publish for Free Agents / First String */}
                    {showUpgradeBanner && !sharingCenterPending && activeSection === 'creations' && (
                      <div className="shrink-0 bg-gradient-to-r from-[#00aef7]/10 to-[#0077cc]/10 border-b border-[#00aef7]/30 px-4 py-3">
                        <div className="flex items-start gap-3">
                          <span className="text-xl shrink-0 mt-0.5">&#127881;</span>
                          <div className="flex-1">
                            <p className="text-sm font-semibold text-gray-800">Get more creations!</p>
                            <p className="text-xs text-gray-600 mt-0.5">
                              Supercharge your networking — upgrade to <strong>First String</strong> and get <strong>5 AI-designed business creations</strong> every month.
                            </p>
                            <div className="flex items-center gap-2 mt-2">
                              <button
                                onClick={() => { setShowUpgradeBanner(false); openUpgradeCheckout('first_string', 'month'); }}
                                className="px-3 py-1 text-xs font-semibold text-white bg-[#00aef7] rounded-lg hover:bg-[#0099dd] transition-colors"
                              >
                                Monthly — $19.99/mo
                              </button>
                              <button
                                onClick={() => { setShowUpgradeBanner(false); openUpgradeCheckout('first_string', 'year'); }}
                                className="px-3 py-1 text-xs font-semibold text-white bg-[#0077cc] rounded-lg hover:bg-[#006ab5] transition-colors"
                              >
                                Annual — $215.89/yr <span className="opacity-75">(save 10%)</span>
                              </button>
                              <button
                                onClick={() => setShowUpgradeBanner(false)}
                                className="px-3 py-1 text-xs font-medium text-gray-500 hover:text-gray-700 transition-colors"
                              >
                                Maybe later
                              </button>
                            </div>
                          </div>
                          <button
                            onClick={() => setShowUpgradeBanner(false)}
                            className="text-gray-400 hover:text-gray-600 text-lg leading-none px-1 shrink-0"
                            title="Dismiss"
                          >&#10005;</button>
                        </div>
                      </div>
                    )}

                    {/* Grid content — shown when not pending */}
                    {!sharingCenterPending && (
                      <div className="flex-1 overflow-auto pb-[200px] scrollbar-thin scrollbar-thumb-gray-200 scrollbar-track-transparent">
                        {/* Column headers — sticky at top, scrolls horizontally with rows */}
                        <div
                          ref={headerRowRef}
                          data-grid-row
                          className="sticky top-0 z-10 grid border-b border-gray-300 bg-gray-100 text-[11px] font-semibold text-gray-600 uppercase tracking-wider select-none"
                          style={{ gridTemplateColumns: gridTemplateCols, minWidth: 'max-content' }}
                        >
                          {activeColumns.map((col, ci) => {
                            const isSorted = sortBy === col.key;
                            return (
                              <div
                                key={col.key}
                                onClick={() => toggleSort(col.key)}
                                className={`relative ${col.center ? 'px-0' : 'px-1.5'} py-1.5 border-r border-gray-300 ${col.noTruncate ? '' : 'truncate'} ${col.center ? 'text-center flex items-center justify-center' : ''} cursor-pointer hover:bg-gray-200 ${isSorted ? 'bg-gray-200 text-[#00aef7]' : ''}`}
                                title={col.tooltip
                                  ? (activePlatform && activePlatform !== 'email'
                                    ? `${col.tooltip} on ${activePlatform === 'twitter' ? 'X' : activePlatform.charAt(0).toUpperCase() + activePlatform.slice(1)}`
                                    : `${col.tooltip} (all platforms)`)
                                  : col.label}
                              >
                                {col.iconImg ? (
                                  <img src={col.iconImg} alt={col.tooltip || ''} className="w-4 h-4 inline-block" />
                                ) : col.key === 'author' ? (
                                  <div className="flex items-center gap-2">
                                    <span>{col.label}</span>
                                    <label
                                      className="flex items-center gap-1 cursor-pointer text-[9px] font-medium text-gray-500 hover:text-[#00aef7] normal-case tracking-normal"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <input
                                        type="checkbox"
                                        checked={showMyPostsOnly}
                                        onChange={(e) => setShowMyPostsOnly(e.target.checked)}
                                        className="w-3 h-3 rounded border-gray-300 text-[#00aef7] focus:ring-[#00aef7] cursor-pointer"
                                      />
                                      Show only my posts
                                    </label>
                                  </div>
                                ) : col.label}
                                {/* Resize handle — sits on the right edge of every column except the last */}
                                {ci < activeColumns.length - 1 && (
                                  <div
                                    onMouseDown={(e) => onResizeMouseDown(e, ci)}
                                    onDoubleClick={(e) => { e.stopPropagation(); onResizeDoubleClick(ci); }}
                                    onClick={(e) => e.stopPropagation()}
                                    className="absolute top-0 right-0 w-[5px] h-full cursor-col-resize hover:bg-[#00aef7]/40 transition-colors z-10"
                                    style={{ transform: 'translateX(50%)' }}
                                  />
                                )}
                              </div>
                            );
                          })}
                        </div>

                        {/* Rows */}
                          {postsLoading && posts.length === 0 ? (
                            // Larger, centered spinner with status text — the
                            // earlier 24px spinner was too small in the full-
                            // size modal and read as a blank loading state
                            // for 2-3 seconds while posts fetched.
                            <div className="flex flex-col items-center justify-center gap-3 py-20 min-h-[300px]">
                              <div className="w-12 h-12 border-4 border-[#00aef7] border-t-transparent rounded-full animate-spin" />
                              <p className="text-sm text-gray-500 font-medium">Loading your creations…</p>
                            </div>
                          ) : (() => {
                            // ── Row-per-creation mode ──
                            // Each row is a creation. The active platform tab determines what shows:
                            //   - For social platforms (facebook/instagram/twitter/linkedin/youtube/tiktok):
                            //     show creations where publishes[platform] exists, OR creations the
                            //     viewer owns (their drafts/un-posted).
                            //   - For email: show all creations the viewer can see (email is unlimited).
                            // Other users never see un-posted creations they don't own.
                            const seenIds = new Set();
                            const dedupedPosts = posts.filter((post) => {
                              if (seenIds.has(post.post_id)) return false;
                              seenIds.add(post.post_id);
                              return true;
                            });

                            const visiblePosts = dedupedPosts.filter((post) => {
                              const isOwner = Number(post.post_u_id) === Number(profile?.u_id);
                              // "Show my posts" filter
                              if (showMyPostsOnly && !isOwner) return false;
                              // Show all creations on all tabs — posted or not.
                              // Toolbar handles posted ("View on X" + Copy Link) vs
                              // not-posted ("Ask Author to Publish" or "Post to X" for owner).
                              return true;
                            });

                            if (dedupedPosts.length === 0) return (
                              <div className="flex items-center justify-center py-12">
                                <p className="text-sm text-gray-400">No published creations yet. Publish from the Design Center to see them here.</p>
                              </div>
                            );

                            if (visiblePosts.length === 0) return (
                              <div className="flex items-center justify-center py-12">
                                <p className="text-sm text-gray-400">No creations have been posted to {activePlatform === 'twitter' ? 'X' : activePlatform.charAt(0).toUpperCase() + activePlatform.slice(1)} yet.</p>
                              </div>
                            );

                            // Apply column sort if a column header was clicked.
                            // Strings compare via localeCompare; everything else
                            // numerically. Default order (sortBy=null) keeps the
                            // server-returned order.
                            const sortedPosts = sortBy ? [...visiblePosts].sort((a, b) => {
                              const av = computeSortValue(a, sortBy, activePlatform);
                              const bv = computeSortValue(b, sortBy, activePlatform);
                              let cmp = 0;
                              if (typeof av === 'string' && typeof bv === 'string') {
                                cmp = av.localeCompare(bv);
                              } else {
                                cmp = av < bv ? -1 : av > bv ? 1 : 0;
                              }
                              return sortDir === 'asc' ? cmp : -cmp;
                            }) : visiblePosts;

                            return sortedPosts.map((post, i) => {
                              const author = [post.u_first_name, post.u_last_name].filter(Boolean).join(' ') || 'Unknown';
                              const title = post.headline || '(untitled)';
                              const category = CATEGORY_LABELS[post.category_key] || post.category_key || '';
                              const industry = post.b_business_industry || '';
                              const date = post.published_at
                                ? new Date(post.published_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                                : '';
                              const isOwner = Number(post.post_u_id) === Number(profile?.u_id);

                              // Check if user has clicked through — platform-specific when a tab is active
                              const publishes = post.publishes || {};
                              const hasAnyClick = activePlatform && activePlatform !== 'email'
                                ? !!publishes[activePlatform]?.viewer_clicked
                                : Object.values(publishes).some(p => p?.viewer_clicked);

                              // Aggregate engagement across all platforms or active platform
                              let totalLikes = 0, totalComments = 0, totalShares = 0, totalViews = 0;
                              const engagementPlatforms = activePlatform && activePlatform !== 'email'
                                ? [activePlatform]
                                : Object.keys(publishes);
                              for (const p of engagementPlatforms) {
                                if (publishes[p]) {
                                  totalLikes += publishes[p].like_count || 0;
                                  totalComments += publishes[p].comment_count || 0;
                                  totalShares += publishes[p].share_count || 0;
                                  totalViews += publishes[p].view_count || 0;
                                }
                              }

                              return (
                                <div
                                  key={`${post.post_id}-${i}`}
                                  data-grid-row
                                  onClick={() => openPostView(post)}
                                  onMouseEnter={(e) => onCreationMouseEnter(post, e)}
                                  onMouseLeave={onCreationMouseLeave}
                                  className={`relative group grid text-xs cursor-pointer hover:bg-[#00aef7]/5 transition-colors ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50/60'}`}
                                  style={{ gridTemplateColumns: gridTemplateCols, minWidth: 'max-content' }}
                                >
                                  {/* Dynamic cells based on active columns */}
                                  {activeColumns.map((col) => {
                                    if (col.key === 'visited') {
                                      const relevantPublishes = activePlatform && activePlatform !== 'email'
                                        ? (publishes[activePlatform] ? [publishes[activePlatform]] : [])
                                        : Object.values(publishes);
                                      const clickDates = relevantPublishes
                                        .filter(p => p?.viewer_clicked && p?.viewer_clicked_at)
                                        .map(p => new Date(p.viewer_clicked_at));
                                      const latestClick = clickDates.length > 0
                                        ? new Date(Math.max(...clickDates)).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                                        : null;
                                      return (
                                        <div key={col.key} className="px-0 py-2.5 border-r border-gray-200 text-center"
                                          title={hasAnyClick ? `You interacted with this post on ${latestClick || 'a recent date'}` : 'Not yet visited'}
                                          onClick={(e) => { if (hasAnyClick) e.stopPropagation(); }}
                                          onMouseEnter={(e) => { e.stopPropagation(); onCreationMouseLeave(); }}
                                        >
                                          {hasAnyClick ? <img src="/images/visited-icon.svg" alt="Visited" className="w-4 h-4 inline-block" /> : <span className="text-gray-300 text-[10px]">○</span>}
                                        </div>
                                      );
                                    }
                                    if (col.key === 'likes') return <div key={col.key} className="px-0 py-2.5 border-r border-gray-200 text-center whitespace-nowrap text-gray-600">{totalLikes > 0 ? <span className="font-medium">{totalLikes.toLocaleString()}</span> : <span className="text-gray-300">0</span>}</div>;
                                    if (col.key === 'comments') return <div key={col.key} className="px-0 py-2.5 border-r border-gray-200 text-center whitespace-nowrap text-gray-600">{totalComments > 0 ? <span className="font-medium">{totalComments.toLocaleString()}</span> : <span className="text-gray-300">0</span>}</div>;
                                    if (col.key === 'shares') return <div key={col.key} className="px-0 py-2.5 border-r border-gray-200 text-center whitespace-nowrap text-gray-600">{totalShares > 0 ? <span className="font-medium">{totalShares.toLocaleString()}</span> : <span className="text-gray-300">0</span>}</div>;
                                    if (col.key === 'views') return <div key={col.key} className="px-0 py-2.5 border-r border-gray-200 text-center whitespace-nowrap text-gray-600">{totalViews > 0 ? <span className="font-medium">{totalViews.toLocaleString()}</span> : <span className="text-gray-300">0</span>}</div>;
                                    if (col.key === 'emailed') return <div key={col.key} className="px-0 py-2.5 border-r border-gray-200 text-center whitespace-nowrap text-gray-500">{post.email_share_count > 0 ? <span className="font-medium text-gray-600">{post.email_share_count}</span> : <span className="text-gray-300">0</span>}</div>;
                                    if (col.key === 'author') return (
                                      <div key={col.key} className="px-1.5 py-2.5 border-r border-gray-200 font-medium text-gray-800 whitespace-nowrap overflow-hidden" title={author}>
                                        <span className="truncate inline-block align-middle max-w-[calc(100%-3rem)]">{author}</span>
                                        {isFreeAgent && (
                                          post.is_team_design
                                            ? <img src={`${process.env.PUBLIC_URL}/pricing/gold_star_badge.png`} alt="Team" title={post.team_name ? `Team: ${post.team_name}` : 'Team'} className="ml-1.5 inline-block w-6 h-6 align-middle shrink-0" />
                                            : post.subscription_tier === 'first_string'
                                              ? <img src={`${process.env.PUBLIC_URL}/pricing/Foam_Finger_badge.png`} alt="First String" title="First String" className="ml-1.5 inline-block w-6 h-6 align-middle shrink-0" />
                                              : <img src={`${process.env.PUBLIC_URL}/pricing/Whistle_badge.png`} alt="Free Agent" title="Free Agent" className="ml-1.5 inline-block w-6 h-6 align-middle shrink-0" />
                                        )}
                                      </div>
                                    );
                                    if (col.key === 'title') return <div key={col.key} className="px-1.5 py-2.5 border-r border-gray-200 truncate font-medium text-gray-800" title={title}>{title}</div>;
                                    if (col.key === 'category') return <div key={col.key} className="px-1.5 py-2.5 border-r border-gray-200 truncate text-gray-600" title={category}>{category}</div>;
                                    if (col.key === 'industry') return <div key={col.key} className="px-1.5 py-2.5 border-r border-gray-200 truncate text-gray-600" title={industry}>{industry || <span className="text-gray-400">—</span>}</div>;
                                    if (col.key === 'date') return <div key={col.key} className="px-1.5 py-2.5 border-r border-gray-200 text-gray-500 whitespace-nowrap">{date}</div>;
                                    return null;
                                  })}
                                  {/* Floating three-dot menu — appears on hover, overlays the row near the author name */}
                                  {isOwner && (
                                    <div className={`absolute left-1 top-1/2 -translate-y-1/2 z-20 ${actionMenuPostId === post.post_id ? '' : 'opacity-0 group-hover:opacity-100'} transition-opacity`}>
                                      <button
                                        onClick={(e) => { e.stopPropagation(); setActionMenuPostId(actionMenuPostId === post.post_id ? null : post.post_id); }}
                                        onMouseDown={(e) => e.stopPropagation()}
                                        className="p-0.5 rounded bg-white shadow border border-gray-200 hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors"
                                        title="Actions"
                                      >
                                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5">
                                          <path d="M10 3a1.5 1.5 0 110 3 1.5 1.5 0 010-3zm0 5.5a1.5 1.5 0 110 3 1.5 1.5 0 010-3zm0 5.5a1.5 1.5 0 110 3 1.5 1.5 0 010-3z" />
                                        </svg>
                                      </button>
                                      {actionMenuPostId === post.post_id && (
                                        <div
                                          onMouseDown={(e) => e.stopPropagation()}
                                          className="absolute left-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 py-1 min-w-[120px]"
                                        >
                                          <button
                                            onClick={(e) => { e.stopPropagation(); setActionMenuPostId(null); setConfirmDeletePostId(post.post_id); }}
                                            className="w-full text-left px-3 py-1.5 text-xs text-red-600 hover:bg-red-50 flex items-center gap-2"
                                          >
                                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5">
                                              <path fillRule="evenodd" d="M8.75 1A2.75 2.75 0 006 3.75v.443c-.795.077-1.584.176-2.365.298a.75.75 0 10.23 1.482l.149-.022.841 10.518A2.75 2.75 0 007.596 19h4.807a2.75 2.75 0 002.742-2.53l.841-10.519.149.023a.75.75 0 00.23-1.482A41.03 41.03 0 0014 4.193V3.75A2.75 2.75 0 0011.25 1h-2.5zM10 4c.84 0 1.673.025 2.5.075V3.75c0-.69-.56-1.25-1.25-1.25h-2.5c-.69 0-1.25.56-1.25 1.25v.325C8.327 4.025 9.16 4 10 4zM8.58 7.72a.75.75 0 00-1.5.06l.3 7.5a.75.75 0 101.5-.06l-.3-7.5zm4.34.06a.75.75 0 10-1.5-.06l-.3 7.5a.75.75 0 101.5.06l.3-7.5z" clipRule="evenodd" />
                                            </svg>
                                            Delete
                                          </button>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              );
                            });
                          })()}
                      </div>
                    )}
                  </>
                )}
                </>)}

                {/* ─── Delete confirmation dialog ─── */}
                {confirmDeletePostId && (
                  <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[100001]" onClick={() => setConfirmDeletePostId(null)}>
                    <div className="bg-white rounded-xl shadow-2xl p-6 max-w-sm w-full mx-4" onClick={(e) => e.stopPropagation()}>
                      <h3 className="text-base font-semibold text-gray-900 mb-2">Delete this creation?</h3>
                      <p className="text-sm text-gray-500 mb-5">This cannot be undone. The creation will be removed from the Sharing Center.</p>
                      <div className="flex justify-end gap-3">
                        <button
                          onClick={() => setConfirmDeletePostId(null)}
                          className="px-4 py-1.5 text-sm text-gray-600 hover:text-gray-800 rounded-lg hover:bg-gray-100"
                        >Cancel</button>
                        <button
                          onClick={() => handleDeletePost(confirmDeletePostId)}
                          disabled={!!deletingPostId}
                          className="px-4 py-1.5 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50"
                        >{deletingPostId ? 'Deleting...' : 'Delete'}</button>
                      </div>
                    </div>
                  </div>
                )}

                {/* ─── PROFILE SECTION (teammate cards grouped by team, or local contacts for FA/FS) ─── */}
                {activeSection === 'profile' && (
                  <div className="flex-1 overflow-y-auto p-4 pb-[200px] scrollbar-thin scrollbar-thumb-gray-200 scrollbar-track-transparent">
                    {/* Search box — type "plumber" to filter to any plumber on
                        any team, or a person's name. Shared state with the
                        company and teams sections. */}
                    <div className="mb-4 relative">
                      <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 10a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Search teammates by name or profession…"
                        className="w-full pl-9 pr-9 py-2 text-sm rounded-lg border border-gray-300 bg-white focus:outline-none focus:ring-2 focus:ring-[#00aef7]/40 focus:border-[#00aef7]"
                      />
                      {searchQuery && (
                        <button
                          type="button"
                          onClick={() => setSearchQuery('')}
                          className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full hover:bg-gray-100 flex items-center justify-center text-gray-400 hover:text-gray-600"
                          title="Clear"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                          </svg>
                        </button>
                      )}
                    </div>

                    {/* When searching: flat deduped grid of unique cards
                        (one per contact, regardless of how many teams they're
                        on). When not searching: team-grouped layout. The same
                        renderProfileMemberCard helper drives both — identical
                        captain styling, ratings, FA/FS popup, click handler. */}
                    {teamsLoading ? (
                      <div className="flex items-center justify-center py-16">
                        <div className="w-8 h-8 border-4 border-[#00aef7] border-t-transparent rounded-full animate-spin" />
                      </div>
                    ) : teamsWithMembers.length === 0 ? (
                      <div className="flex items-center justify-center py-16">
                        <p className="text-sm text-gray-400">No teams found.</p>
                      </div>
                    ) : searchQuery.trim() ? (() => {
                      const matches = flattenMatchingMembers(teamsWithMembers, searchQuery);
                      if (matches.length === 0) {
                        return (
                          <div className="flex items-center justify-center py-12">
                            <p className="text-sm text-gray-400">No matches for "{searchQuery}"</p>
                          </div>
                        );
                      }
                      return (
                        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-3">
                          {matches.map((member) => renderProfileMemberCard(member, member._team_ctx?.captain_u_id ?? null))}
                        </div>
                      );
                    })() : (
                      <div className="space-y-4">
                        {teamsWithMembers.map((team) => {
                          const isCollapsed = collapsedTeams[team.team_id];
                          const sortedMembers = [...(team.members || [])].sort((a, b) => {
                            const aCap = Number(a.u_id) === Number(team.captain_u_id);
                            const bCap = Number(b.u_id) === Number(team.captain_u_id);
                            return aCap === bCap ? 0 : aCap ? -1 : 1;
                          });
                          return (
                            <div key={team.team_id} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                              {/* Team header — collapse/expand */}
                              <button
                                onClick={() => toggleTeamCollapse(team.team_id)}
                                className="w-full flex items-center justify-between px-4 py-2.5 bg-gray-50 hover:bg-gray-100 transition-colors"
                              >
                                <span className="text-sm font-semibold text-gray-700">{team.team_name || 'Unnamed Team'}</span>
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  className={`w-4 h-4 text-gray-400 transition-transform ${isCollapsed ? '' : 'rotate-180'}`}
                                  viewBox="0 0 20 20"
                                  fill="currentColor"
                                >
                                  <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                              </button>

                              {/* Members grid — captain first */}
                              {!isCollapsed && (
                                <div className="p-3 grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-3">
                                  {sortedMembers.length === 0 ? (
                                    <p className="col-span-full text-center text-xs text-gray-400 py-4">No teammates in this team.</p>
                                  ) : (
                                    sortedMembers.map((member) => renderProfileMemberCard(member, team.captain_u_id))
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {/* ─── COMPANY SECTION (teammate cards — hover/click shows company profile) ─── */}
                {activeSection === 'company' && (
                  <div className="flex-1 overflow-y-auto p-4 pb-[200px] scrollbar-thin scrollbar-thumb-gray-200 scrollbar-track-transparent">
                    {/* Search box — same shared state as profile section. */}
                    <div className="mb-4 relative">
                      <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 10a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Search companies by name or industry…"
                        className="w-full pl-9 pr-9 py-2 text-sm rounded-lg border border-gray-300 bg-white focus:outline-none focus:ring-2 focus:ring-[#00aef7]/40 focus:border-[#00aef7]"
                      />
                      {searchQuery && (
                        <button
                          type="button"
                          onClick={() => setSearchQuery('')}
                          className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full hover:bg-gray-100 flex items-center justify-center text-gray-400 hover:text-gray-600"
                          title="Clear"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                          </svg>
                        </button>
                      )}
                    </div>

                    {/* When searching: flat grid with one card per matching
                        company (deduped by b_id). When not searching: the
                        original team-grouped layout. Both branches use the
                        same renderCompanyMemberCard helper. */}
                    {teamsLoading ? (
                      <div className="flex items-center justify-center py-16">
                        <div className="w-8 h-8 border-4 border-[#00aef7] border-t-transparent rounded-full animate-spin" />
                      </div>
                    ) : teamsWithMembers.length === 0 ? (
                      <div className="flex items-center justify-center py-16">
                        <p className="text-sm text-gray-400">No teams found.</p>
                      </div>
                    ) : searchQuery.trim() ? (() => {
                      const matches = flattenMatchingCompanies(teamsWithMembers, searchQuery);
                      if (matches.length === 0) {
                        return (
                          <div className="flex items-center justify-center py-12">
                            <p className="text-sm text-gray-400">No matches for "{searchQuery}"</p>
                          </div>
                        );
                      }
                      return (
                        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-3">
                          {matches.map((member) => renderCompanyMemberCard(member))}
                        </div>
                      );
                    })() : (
                      <div className="space-y-4">
                        {teamsWithMembers.map((team) => {
                          const isCollapsed = collapsedTeams[team.team_id];
                          const teamMembers = team.members || [];
                          return (
                            <div key={team.team_id} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                              <button
                                onClick={() => toggleTeamCollapse(team.team_id)}
                                className="w-full flex items-center justify-between px-4 py-2.5 bg-gray-50 hover:bg-gray-100 transition-colors"
                              >
                                <span className="text-sm font-semibold text-gray-700">{team.team_name || 'Unnamed Team'}</span>
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  className={`w-4 h-4 text-gray-400 transition-transform ${isCollapsed ? '' : 'rotate-180'}`}
                                  viewBox="0 0 20 20"
                                  fill="currentColor"
                                >
                                  <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                              </button>

                              {!isCollapsed && (
                                <div className="p-3 grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-3">
                                  {teamMembers.length === 0 ? (
                                    <p className="col-span-full text-center text-xs text-gray-400 py-4">No teammates in this team.</p>
                                  ) : (
                                    teamMembers.map((member) => renderCompanyMemberCard(member))
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {/* ─── TEAMS SECTION (team cards — hover/click shows team profile) ─── */}
                {activeSection === 'teams' && (
                  <div className="flex-1 overflow-y-auto p-4 pb-[200px] scrollbar-thin scrollbar-thumb-gray-200 scrollbar-track-transparent">
                    {/* Search box. When the user types, the layout pivots
                        from team cards to contact cards. */}
                    <div className="mb-4 relative">
                      <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 10a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Search teams or teammates by name or profession…"
                        className="w-full pl-9 pr-9 py-2 text-sm rounded-lg border border-gray-300 bg-white focus:outline-none focus:ring-2 focus:ring-[#00aef7]/40 focus:border-[#00aef7]"
                      />
                      {searchQuery && (
                        <button
                          type="button"
                          onClick={() => setSearchQuery('')}
                          className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full hover:bg-gray-100 flex items-center justify-center text-gray-400 hover:text-gray-600"
                          title="Clear"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                          </svg>
                        </button>
                      )}
                    </div>

                    {/* Search results — flat grid of CONTACT cards (pivoted
                        from the team-card layout). Reuses the SAME card as
                        the profile section (renderProfileMemberCard) so
                        captain styling, ratings, FA/FS popup, click handler
                        — all identical to no-search profile cards. */}
                    {searchQuery.trim() && (() => {
                      const matches = flattenMatchingMembers(teamsWithMembers, searchQuery);
                      if (matches.length === 0) {
                        return (
                          <div className="flex items-center justify-center py-12">
                            <p className="text-sm text-gray-400">No matches for "{searchQuery}"</p>
                          </div>
                        );
                      }
                      return (
                        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-3">
                          {matches.map((member) => renderProfileMemberCard(member, member._team_ctx?.captain_u_id ?? null))}
                        </div>
                      );
                    })()}

                    {/* Existing team-card grid — unchanged. CSS-hidden when
                        searching so DOM tree stays mounted. */}
                    <div className={searchQuery.trim() ? 'hidden' : ''}>
                    {teamsLoading ? (
                      <div className="flex items-center justify-center py-16">
                        <div className="w-8 h-8 border-4 border-[#00aef7] border-t-transparent rounded-full animate-spin" />
                      </div>
                    ) : teamsWithMembers.length === 0 ? (
                      <div className="flex items-center justify-center py-16">
                        <p className="text-sm text-gray-400">No teams found.</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                        {teamsWithMembers.map((team) => {
                          const members = team.members || [];
                          const displayMembers = members.slice(0, 5);
                          const extraCount = members.length > 5 ? members.length - 5 : 0;
                          const teamLocation = (() => {
                            const counts = {};
                            members.forEach(m => {
                              const loc = [m.b_city, m.b_state].filter(Boolean).join(', ');
                              if (loc) counts[loc] = (counts[loc] || 0) + 1;
                            });
                            const entries = Object.entries(counts);
                            if (entries.length === 0) return '';
                            entries.sort((a, b) => b[1] - a[1]);
                            return entries[0][0];
                          })();

                          // Team cooldown check
                          const teamCooldown = activePlatform && activePlatform !== 'email' && cooldowns.teams[team.team_id];
                          const platformLabel = activePlatform === 'twitter' ? 'X' : activePlatform ? activePlatform.charAt(0).toUpperCase() + activePlatform.slice(1) : '';
                          const isTeamBlocked = !!teamCooldown;
                          const teamBlockedReason = teamCooldown
                            ? `You already shared ${team.team_name || 'this team'} on ${platformLabel} recently. Each team can only be shared once per platform every 30 days. You can share again in ${teamCooldown.days_remaining} day${teamCooldown.days_remaining === 1 ? '' : 's'}.`
                            : '';
                          const isTeamCooldownPopupOpen = cooldownPopup?.kind === 'team' && Number(cooldownPopup.id) === Number(team.team_id);
                          const teamCooldownUrl = teamCooldown?.platform_post_url || null;
                          const isTeamVisuallyBlocked = isTeamBlocked && activePlatform && activePlatform !== 'email';
                          const shortTeamBlockedReason = teamCooldown
                            ? `Already shared on ${platformLabel}. Try again in ${teamCooldown.days_remaining} day${teamCooldown.days_remaining === 1 ? '' : 's'}.`
                            : '';

                          return (
                            <div
                              key={team.team_id}
                              onClick={() => {
                                if (isTeamBlocked && activePlatform && activePlatform !== 'email') {
                                  setCooldownPopup({ kind: 'team', id: team.team_id });
                                  return;
                                }
                                setShareModalTeam(team);
                                setHoveredTeam(null);
                              }}
                              onMouseEnter={(e) => onTeamCardMouseEnter(team, e)}
                              onMouseLeave={onTeamCardMouseLeave}
                              title={isTeamVisuallyBlocked ? teamBlockedReason : ''}
                              className={`relative rounded-lg p-3 flex flex-col items-center text-center bg-white ${isTeamVisuallyBlocked && !isTeamCooldownPopupOpen ? 'opacity-40' : 'hover:shadow-lg'} cursor-pointer transition-shadow`}
                              style={{
                                border: '1px solid rgba(0, 174, 247, 0.25)',
                                borderBottomWidth: '2px',
                                boxShadow: '0 2px 4px -1px rgba(0, 174, 247, 0.15), inset 0 -6px 8px -6px rgba(0, 174, 247, 0.12)',
                              }}
                            >
                              {/* Stacked member avatars */}
                              <div className="flex items-center justify-center mb-2">
                                {displayMembers.map((m, i) => {
                                  const mPhoto = m.u_profile_picture || '';
                                  return (
                                    <div
                                      key={m.u_id || i}
                                      className="rounded-full overflow-hidden border-2 border-white bg-gray-100 shrink-0"
                                      style={{ width: 28, height: 28, marginLeft: i > 0 ? -6 : 0, zIndex: displayMembers.length - i }}
                                    >
                                      {mPhoto ? (
                                        <img
                                          src={`${process.env.REACT_APP_API_URL}${mPhoto}?t=${IMG_CACHE_KEY}`}
                                          alt=""
                                          className="w-full h-full object-cover"
                                        />
                                      ) : (
                                        <div className="w-full h-full flex items-center justify-center text-gray-400 bg-gray-200 text-[8px]">
                                          {[m.u_first_name, m.u_last_name].filter(Boolean).map(w => w[0]).join('').slice(0, 2).toUpperCase() || '?'}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                                {extraCount > 0 && (
                                  <div
                                    className="rounded-full bg-gray-200 border-2 border-white flex items-center justify-center text-gray-600 font-bold shrink-0 text-[9px]"
                                    style={{ width: 28, height: 28, marginLeft: -6, zIndex: 0 }}
                                  >
                                    +{extraCount}
                                  </div>
                                )}
                              </div>

                              <div className="space-y-0.5 w-full">
                                <div className="font-semibold text-xs truncate">{team.team_name || 'Unnamed Team'}</div>
                                <div className="text-[11px] text-gray-500">
                                  {members.length} {members.length === 1 ? 'member' : 'members'}
                                </div>
                                {teamLocation && (
                                  <div className="text-[10px] text-gray-400 flex items-center justify-center gap-0.5">
                                    <span>&#128205;</span> {teamLocation}
                                  </div>
                                )}
                                {(() => {
                                  let totalScore = 0, totalCount = 0;
                                  members.forEach(m => {
                                    const avg = Number(m.rating_avg || m.u_avg_rating || 0);
                                    const cnt = Number(m.rating_count || m.u_review_count || 0);
                                    if (avg > 0 && cnt > 0) { totalScore += avg * cnt; totalCount += cnt; }
                                  });
                                  if (totalCount === 0) return null;
                                  const teamAvg = totalScore / totalCount;
                                  const full = Math.floor(teamAvg);
                                  const half = teamAvg - full >= 0.25;
                                  const empty = 5 - full - (half ? 1 : 0);
                                  return (
                                    <div className="flex flex-col items-center mt-0.5" style={{ fontSize: 10 }}>
                                      <div className="flex items-center gap-px">
                                        {Array.from({ length: full }).map((_, i) => <span key={`f${i}`} className="text-amber-400">&#9733;</span>)}
                                        {half && <span className="text-amber-400">&#9733;</span>}
                                        {Array.from({ length: empty }).map((_, i) => <span key={`e${i}`} className="text-gray-300">&#9733;</span>)}
                                      </div>
                                      <div className="text-[9px] text-gray-400">{totalCount} review{totalCount === 1 ? '' : 's'}</div>
                                    </div>
                                  );
                                })()}
                              </div>

                              {/* Cooldown popup — same pattern as profile/company. */}
                              {isTeamCooldownPopupOpen && (
                                <div
                                  className="absolute inset-0 z-20 rounded-lg bg-white flex flex-col items-center justify-center gap-1.5 p-2 shadow-lg ring-1 ring-[#00aef7]/40"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <p className="text-[11px] text-gray-800 text-center leading-snug font-medium">
                                    {shortTeamBlockedReason}
                                  </p>
                                  {teamCooldownUrl && (
                                    <a
                                      href={teamCooldownUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      onClick={() => setCooldownPopup(null)}
                                      className="w-full px-2 py-1 text-[11px] font-semibold rounded-md text-white bg-[#00aef7] hover:bg-[#0097d6] text-center"
                                    >
                                      View the post &rarr;
                                    </a>
                                  )}
                                  <button
                                    onClick={() => setCooldownPopup(null)}
                                    className="text-[10px] text-gray-500 hover:text-gray-700"
                                  >
                                    Close
                                  </button>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                    </div>{/* close searchQuery hidden wrapper */}
                  </div>
                )}

              </div>{/* end right panel */}
            </div>{/* end two-column area */}
          </div>{/* end body */}

          {/* ─── Hover tooltip: platform preview (profile, company, or team) ─── */}
          {hoveredMember && activePlatform && (activeSection === 'profile' || activeSection === 'company') && (() => {
            const frameMap = activeSection === 'company' ? COMPANY_FRAME_MAP : PROFILE_FRAME_MAP;
            const FrameComp = frameMap[activePlatform];
            if (!FrameComp) return null;

            const caption = activeSection === 'company'
              ? 'I fully recommend this company. We have used their services.'
              : 'Check out one of my teammates on eHandshake';

            return (
              <div className="absolute right-[108px] top-[calc(50%+50px)] -translate-y-1/2 pointer-events-none" style={{ zIndex: 99999, filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.15))' }}>
                <div style={{ transform: 'scale(0.65)', transformOrigin: 'top right' }}>
                  <FrameComp member={hoveredMember.member} caption={caption} biz={sharerBiz} />
                </div>
              </div>
            );
          })()}

          {/* ─── Hover tooltip: team preview ─── */}
          {hoveredTeam && activePlatform && activeSection === 'teams' && (() => {
            const FrameComp = TEAM_FRAME_MAP[activePlatform];
            if (!FrameComp) return null;

            return (
              <div className="absolute right-[108px] top-[calc(50%+50px)] -translate-y-1/2 pointer-events-none" style={{ zIndex: 99999, filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.15))' }}>
                <div style={{ transform: 'scale(0.65)', transformOrigin: 'top right' }}>
                  <FrameComp team={hoveredTeam.team} caption="Check out our amazing team on eHandshake!" biz={sharerBiz} />
                </div>
              </div>
            );
          })()}


        </div>
      </Rnd>

      {/* ─── Profile share modal ─── */}
      {shareModalMember && activePlatform && (
        <ProfileShareModal
          member={shareModalMember}
          platform={activePlatform}
          biz={sharerBiz}
          socialConnections={socialConnections}
          fetchSocialStatus={fetchSocialStatus}
          onClose={() => setShareModalMember(null)}
          onShare={async (data) => {
            const pLabel = data.platform === 'twitter' ? 'X' : data.platform === 'email' ? 'Email' : data.platform.charAt(0).toUpperCase() + data.platform.slice(1);
            if (data.platform === 'email') {
              const res = await fetch(`${API_URL}/api/sharing/profile/email`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                  memberId: data.member.u_id,
                  recipientEmail: data.emailTo,
                  subject: data.emailSubject,
                  message: data.caption,
                }),
              });
              const result = await res.json();
              if (!result.ok) throw new Error(result.error || 'Failed to send');
              setPostSuccess(`Email sent successfully!`);
              setTimeout(() => setPostSuccess(''), 4000);
            } else {
              const res = await fetch(`${API_URL}/api/sharing/profile/social`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                  memberId: data.member.u_id,
                  platform: data.platform,
                  caption: data.caption,
                }),
              });
              const result = await res.json();
              if (!result.ok) throw new Error(result.error || 'Failed to post');
              setPostSuccess(`Successfully shared profile to ${pLabel}!`);
              setTimeout(() => setPostSuccess(''), 4000);
              // Refresh cooldowns so the card grays out immediately
              try {
                const cdRes = await fetch(`${API_URL}/api/sharing/cooldowns?platform=${data.platform}`, { credentials: 'include' });
                const cdData = await cdRes.json();
                if (cdData.ok) setCooldowns(cdData.cooldowns);
              } catch {}
            }
          }}
        />
      )}

      {/* ─── Company share modal ─── */}
      {companyShareModalMember && activePlatform && (
        <CompanyShareModal
          member={companyShareModalMember}
          platform={activePlatform}
          biz={sharerBiz}
          socialConnections={socialConnections}
          fetchSocialStatus={fetchSocialStatus}
          onClose={() => setCompanyShareModalMember(null)}
          onShare={async (data) => {
            const pLabel = data.platform === 'twitter' ? 'X' : data.platform === 'email' ? 'Email' : data.platform.charAt(0).toUpperCase() + data.platform.slice(1);
            if (data.platform === 'email') {
              const res = await fetch(`${API_URL}/api/sharing/company/email`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                  memberId: data.member.u_id,
                  recipientEmail: data.emailTo,
                  subject: data.emailSubject,
                  message: data.caption,
                }),
              });
              const result = await res.json();
              if (!result.ok) throw new Error(result.error || 'Failed to send');
              setPostSuccess(`Email sent successfully!`);
              setTimeout(() => setPostSuccess(''), 4000);
            } else {
              const res = await fetch(`${API_URL}/api/sharing/company/social`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                  memberId: data.member.u_id,
                  platform: data.platform,
                  caption: data.caption,
                }),
              });
              const result = await res.json();
              if (!result.ok) throw new Error(result.error || 'Failed to post');
              setPostSuccess(`Successfully shared company to ${pLabel}!`);
              setTimeout(() => setPostSuccess(''), 4000);
              try {
                const cdRes = await fetch(`${API_URL}/api/sharing/cooldowns?platform=${data.platform}`, { credentials: 'include' });
                const cdData = await cdRes.json();
                if (cdData.ok) setCooldowns(cdData.cooldowns);
              } catch {}
            }
          }}
        />
      )}

      {/* ─── Team share modal ─── */}
      {shareModalTeam && activePlatform && (
        <TeamShareModal
          team={shareModalTeam}
          platform={activePlatform}
          biz={sharerBiz}
          socialConnections={socialConnections}
          fetchSocialStatus={fetchSocialStatus}
          onClose={() => setShareModalTeam(null)}
          onShare={async (data) => {
            const pLabel = data.platform === 'twitter' ? 'X' : data.platform === 'email' ? 'Email' : data.platform.charAt(0).toUpperCase() + data.platform.slice(1);
            if (data.platform === 'email') {
              const res = await fetch(`${API_URL}/api/sharing/team/email`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                  teamId: data.team.team_id,
                  recipientEmail: data.emailTo,
                  subject: data.emailSubject,
                  message: data.caption,
                }),
              });
              const result = await res.json();
              if (!result.ok) throw new Error(result.error || 'Failed to send');
              setPostSuccess(`Email sent successfully!`);
              setTimeout(() => setPostSuccess(''), 4000);
            } else {
              const res = await fetch(`${API_URL}/api/sharing/team/social`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                  teamId: data.team.team_id,
                  platform: data.platform,
                  caption: data.caption,
                }),
              });
              const result = await res.json();
              if (!result.ok) throw new Error(result.error || 'Failed to post');
              setPostSuccess(`Successfully shared team to ${pLabel}!`);
              setTimeout(() => setPostSuccess(''), 4000);
              try {
                const cdRes = await fetch(`${API_URL}/api/sharing/cooldowns?platform=${data.platform}`, { credentials: 'include' });
                const cdData = await cdRes.json();
                if (cdData.ok) setCooldowns(cdData.cooldowns);
              } catch {}
            }
          }}
        />
      )}

      {/* ─── Creation email modal ─── */}
      {creationEmailModal && (() => {
        const sharerName = [profile?.u_first_name, profile?.u_last_name].filter(Boolean).join(' ') || 'eHandshake User';
        return (
          <CreationEmailModalInline
            post={creationEmailModal}
            sharerName={sharerName}
            sending={creationEmailSending}
            onClose={() => setCreationEmailModal(null)}
            onSend={handleCreationEmailSend}
          />
        );
      })()}

      {/* ─── Social Post Preview Modal (Facebook / X / future platforms) ──── */}
      {showPostModal && viewingPost && (() => {
        const conn = socialConnections?.[activePlatform];
        const isConnected = conn?.connected;
        const FrameComp = CREATION_FRAME_MAP[activePlatform];
        const connectedPages = (conn?.pages || []).filter(p => p.connected);
        const hasMultiplePages = connectedPages.length > 1;
        const isTwitter = activePlatform === 'twitter';
        const isYoutube = activePlatform === 'youtube';
        const isLinkedin = activePlatform === 'linkedin';
        const isTiktok = activePlatform === 'tiktok';
        const platformLabel = isTwitter ? 'X' : isYoutube ? 'YouTube' : isLinkedin ? 'LinkedIn' : isTiktok ? 'TikTok' : activePlatform.charAt(0).toUpperCase() + activePlatform.slice(1);

        // Resolve which page/account name to display in the preview
        const activePageId = selectedPageId || connectedPages[0]?.pageId || null;
        const activePageObj = connectedPages.find(p => p.pageId === activePageId) || connectedPages[0];
        const pageName = isTwitter
          ? (conn?.accountName || conn?.pageName || sharerBiz.name)
          : (activePageObj?.pageName || conn?.pageName || sharerBiz.name);
        const xHandle = isTwitter ? (connectedPages[0]?.pageName || conn?.pageName || '') : '';

        // Parse captions
        const captions = viewingPost.captions_json;
        const parsed = typeof captions === 'string' ? (() => { try { return JSON.parse(captions); } catch { return null; } })() : captions;
        const captionText = parsed?.[activePlatform] || parsed?.facebook || parsed?.youtube || parsed?.instagram || (parsed ? Object.values(parsed)[0] : '') || '';
        const creatorName = [viewingPost.u_first_name, viewingPost.u_last_name].filter(Boolean).join(' ');
        const creatorBizName = viewingPost.b_name || '';
        const siteOrigin = window.location.origin.replace(/:\d+$/, '').replace('http://', 'https://');
        const publicSiteOrigin = 'https://ehandshake.com';
        const creatorProfileUrl = viewingPost.u_url_name ? `${publicSiteOrigin}/userprofiles/users/${viewingPost.u_url_name}` : '';
        const creatorBizUrl = viewingPost.b_website ? `${publicSiteOrigin}/userprofiles/companies/${viewingPost.b_website.replace(/\/+$/, '')}` : '';
        const postLink = viewingPost.landing_page_slug
          ? `${siteOrigin}/p/${viewingPost.landing_page_slug}`
          : viewingPost.external_url || '';

        // X character limit tracking
        const tcoLinkChars = viewingPost.landing_page_slug || viewingPost.external_url ? 25 : 0; // 23 t.co + 2 newlines
        const maxChars = isTwitter ? 280 - tcoLinkChars : Infinity;
        const fullDraft = [
          captionText || '',
          creatorName ? `\n— Created by ${creatorName}` : '',
        ].join('');
        const charCount = fullDraft.length;
        const overLimit = isTwitter && charCount > maxChars;

        // Handle Post click — if not connected, open OAuth first, then auto-post
        const handlePostClick = () => {
          setPostError('');
          if (isConnected) {
            handleSharePost();
          } else {
            postAfterConnectRef.current = true;
            setSocialConnecting(true);
            const w = 600, h = 700;
            const left = Math.round(window.screenX + (window.outerWidth - w) / 2);
            const top = Math.round(window.screenY + (window.outerHeight - h) / 2);
            oauthPopupRef.current = window.open(
              `${API_URL}/api/social/${activePlatform}/connect`,
              'social-connect',
              `width=${w},height=${h},left=${left},top=${top},toolbar=no,menubar=no`
            );
          }
        };

        // Platform-specific button colors
        const btnBg = isTiktok ? 'bg-black' : isTwitter ? 'bg-black' : isYoutube ? 'bg-[#FF0000]' : isLinkedin ? 'bg-[#0A66C2]' : 'bg-[#1877F2]';
        const btnHover = isTiktok ? 'hover:bg-gray-800' : isTwitter ? 'hover:bg-gray-800' : isYoutube ? 'hover:bg-[#CC0000]' : isLinkedin ? 'hover:bg-[#004182]' : 'hover:bg-[#166FE5]';
        const btnLoading = isTiktok ? 'bg-black/60' : isTwitter ? 'bg-black/60' : isYoutube ? 'bg-[#FF0000]/60' : isLinkedin ? 'bg-[#0A66C2]/60' : 'bg-[#1877F2]/60';

        return (
          <div
            className="fixed inset-0 bg-black/50 flex items-center justify-center pointer-events-auto"
            style={{ zIndex: 100000 }}
            onClick={(e) => { if (e.target === e.currentTarget && !socialConnecting && !shareLoading) { setShowPostModal(false); setSharerMessage(''); setPostError(''); postAfterConnectRef.current = false; } }}
          >
            <div className="bg-white rounded-xl shadow-2xl flex flex-col overflow-hidden relative" style={{ width: 520, maxWidth: '94vw', maxHeight: '90vh' }}>

              {/* Posting overlay — grays out the entire modal while OAuth is open or API call is in-flight */}
              {(socialConnecting || shareLoading) && (
                <div className="absolute inset-0 bg-white/80 flex flex-col items-center justify-center z-50 rounded-xl">
                  <div className="w-10 h-10 border-4 border-gray-200 border-t-[#00aef7] rounded-full animate-spin mb-3" />
                  <p className="text-sm font-medium text-gray-600">
                    {socialConnecting ? 'Connecting to ' + platformLabel + '…' : 'Posting to ' + platformLabel + '…'}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">Please wait</p>
                </div>
              )}

              {/* Header */}
              <div className="shrink-0 flex items-center justify-between px-5 py-3 border-b border-gray-200 bg-gray-50">
                <div className="flex items-center gap-3">
                  <h3 className="text-sm font-semibold text-gray-800">
                    Post to {platformLabel}
                  </h3>
                  {isConnected ? (
                    <span className="inline-flex items-center gap-1 text-[10px] text-emerald-600 font-medium">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> Connected{isTwitter && xHandle ? ` as ${xHandle}` : ''}
                      <button
                        onClick={async (e) => {
                          e.stopPropagation();
                          const btn = e.currentTarget;
                          // Two-click confirm: first click shows "Sure?", second click disconnects
                          if (btn.dataset.confirm !== 'yes') {
                            btn.dataset.confirm = 'yes';
                            btn.textContent = 'Sure?';
                            btn.className = 'ml-1 text-[10px] text-red-500 font-semibold underline transition-colors';
                            setTimeout(() => { btn.dataset.confirm = ''; btn.textContent = 'Disconnect'; btn.className = 'ml-1 text-[10px] text-gray-400 hover:text-red-500 underline transition-colors'; }, 3000);
                            return;
                          }
                          btn.disabled = true;
                          btn.textContent = 'Disconnecting…';
                          try {
                            const resp = await fetch(`${API_URL}/api/social/${activePlatform}/disconnect`, { method: 'DELETE', credentials: 'include' });
                            const data = await resp.json().catch(() => ({}));
                            if (!resp.ok) {
                              setPostError(data.error || `Failed to disconnect ${platformLabel}. Please try again.`);
                              return;
                            }
                            fetchSocialStatus();
                          } catch (err) {
                            console.error('[SharingCenter] disconnect error:', err);
                            setPostError(`Network error disconnecting ${platformLabel}. Please try again.`);
                          } finally {
                            btn.disabled = false;
                            btn.dataset.confirm = '';
                            btn.textContent = 'Disconnect';
                            btn.className = 'ml-1 text-[10px] text-gray-400 hover:text-red-500 underline transition-colors';
                          }
                        }}
                        className="ml-1 text-[10px] text-gray-400 hover:text-red-500 underline transition-colors"
                        title={`Disconnect ${platformLabel}`}
                      >
                        Disconnect
                      </button>
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-[10px] text-amber-600 font-medium">
                      <span className="w-1.5 h-1.5 rounded-full bg-amber-400" /> Not connected
                    </span>
                  )}
                </div>
                <button onClick={() => { if (!socialConnecting && !shareLoading) { setShowPostModal(false); setSharerMessage(''); setPostError(''); postAfterConnectRef.current = false; } }} className={`text-lg leading-none ${socialConnecting || shareLoading ? 'text-gray-200 cursor-not-allowed' : 'text-gray-400 hover:text-gray-600'}`} title="Close" disabled={socialConnecting || shareLoading}>&#10005;</button>
              </div>

              {/* Body */}
              <div className="flex-1 overflow-y-auto p-5">

                {/* Inline error message (replaces browser alert dialogs) */}
                {postError && (
                  <div className="mb-3 flex items-start gap-2 px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                    <span className="shrink-0 mt-0.5">&#9888;</span>
                    <span className="flex-1">{postError}</span>
                    <button onClick={() => setPostError('')} className="shrink-0 text-red-400 hover:text-red-600 text-lg leading-none">&times;</button>
                  </div>
                )}


                {/* Page/account selector — for any platform with multiple pages */}
                {hasMultiplePages && (
                  <div className="mb-4">
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Post to page</label>
                    <select
                      value={activePageId || ''}
                      onChange={(e) => setSelectedPageId(e.target.value)}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 bg-white focus:border-[#00aef7] focus:ring-1 focus:ring-[#00aef7] outline-none"
                    >
                      {connectedPages.map(p => (
                        <option key={p.pageId} value={p.pageId}>{p.pageName}</option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Character count for X/Twitter */}
                {isTwitter && (
                  <div className="mb-3 flex justify-end">
                    <span className={`text-[10px] font-medium ${overLimit ? 'text-red-500' : charCount > maxChars * 0.9 ? 'text-amber-500' : 'text-gray-400'}`}>
                      {charCount}/{maxChars}
                    </span>
                  </div>
                )}

                {/* Live post preview */}
                <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">

                  {/* Poster header */}
                  <div className="px-3 pt-3 pb-2 flex items-center gap-2.5">
                    {isTwitter ? (
                      /* X-style avatar (dark circle) */
                      <div className="w-9 h-9 rounded-full bg-black flex items-center justify-center text-white text-[10px] font-bold shrink-0">
                        {sharerBiz.initials}
                      </div>
                    ) : (
                      /* Facebook-style avatar (blue gradient) */
                      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white text-[10px] font-bold shrink-0">
                        {sharerBiz.initials}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] font-semibold text-gray-900">
                        {isTwitter ? (pageName || sharerBiz.name) : pageName}
                      </div>
                      <div className="text-[11px] text-gray-500">
                        {isTwitter ? (xHandle ? xHandle : 'Just now') : 'Just now · 🌐'}
                      </div>
                    </div>
                    {isTwitter ? (
                      /* X logo */
                      <svg className="w-4 h-4 text-gray-400 shrink-0" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                      </svg>
                    ) : (
                      <svg className="w-5 h-5 text-gray-300 shrink-0" viewBox="0 0 20 20" fill="currentColor"><circle cx="10" cy="4" r="2"/><circle cx="10" cy="10" r="2"/><circle cx="10" cy="16" r="2"/></svg>
                    )}
                  </div>

                  {/* Post caption (live preview) */}
                  {captionText && (
                    <div className="px-3 pb-2">
                      <p className="text-[13px] text-gray-800 leading-snug whitespace-pre-wrap">
                        {captionText}
                      </p>
                    </div>
                  )}

                  {/* Creation image + headline */}
                  <div className="mb-3 overflow-hidden bg-gray-50 border-t border-gray-100">
                    {FrameComp && (
                      <FrameComp
                        src={viewingPost.asset_type === 'video' && viewingPost.asset_thumb_relpath
                          ? `${API_URL}/uploads/designcenter/${viewingPost.asset_thumb_relpath}`
                          : assetUrl(viewingPost.image_asset_id)}
                        position={{ x: 50, y: 50 }}
                        zoom={1}
                        headline={viewingPost.headline || ''}
                        headlinePos={(() => {
                          const po = viewingPost.platform_overlays_json?.[activePlatform];
                          return {
                            x: po?.posX ?? viewingPost.headline_style_json?.posX ?? 50,
                            y: po?.posY ?? viewingPost.headline_style_json?.posY ?? 71,
                          };
                        })()}
                        headlineStyle={(() => {
                          const po = viewingPost.platform_overlays_json?.[activePlatform];
                          return po
                            ? { ...(viewingPost.headline_style_json || {}), ...po }
                            : (viewingPost.headline_style_json || { fontSize: 14, color: '#FFFFFF', bgOpacity: 0.6, boxWidth: 60 });
                        })()}
                        caption={[
                          captionText,
                          (creatorName || creatorBizName) ? `— Created by ${creatorName}${creatorBizName ? `, ${creatorBizName}` : ''}` : '',
                          creatorProfileUrl ? `👤 ${creatorProfileUrl}` : '',
                          creatorBizUrl ? `🏢 ${creatorBizUrl}` : '',
                          postLink || '',
                        ].filter(Boolean).join('\n')}
                        biz={{
                          name: creatorName || 'Creator',
                          handle: makeHandle(creatorName),
                          initials: makeInitials(creatorName),
                          location: '',
                        }}
                        assetType="image"
                      />
                    )}
                  </div>

                  {/* Fake engagement bar — platform-specific */}
                  <div className="px-3 py-1.5 flex items-center justify-between text-[11px] text-gray-400 border-t border-gray-100">
                    {isTwitter ? (
                      <>
                        <div className="flex items-center gap-4">
                          <span>💬 0</span>
                          <span>🔁 0</span>
                          <span>❤️ 0</span>
                        </div>
                        <span>📊 0</span>
                      </>
                    ) : (
                      <>
                        <div className="flex items-center gap-1"><span>👍</span><span>❤️</span></div>
                        <div>0 comments · 0 shares</div>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="shrink-0 flex items-center justify-between px-5 py-3 border-t border-gray-200 bg-gray-50">
                <button
                  onClick={() => { if (!socialConnecting && !shareLoading) { setShowPostModal(false); setSharerMessage(''); setPostError(''); postAfterConnectRef.current = false; } }}
                  disabled={socialConnecting || shareLoading}
                  className={`px-4 py-1.5 text-sm font-medium transition-colors ${socialConnecting || shareLoading ? 'text-gray-300 cursor-not-allowed' : 'text-gray-600 hover:text-gray-800'}`}
                >
                  Cancel
                </button>
                <button
                  onClick={handlePostClick}
                  disabled={shareLoading || socialConnecting || overLimit}
                  className={`px-6 py-1.5 text-sm font-semibold text-white rounded-lg transition-colors ${
                    overLimit
                      ? 'bg-gray-300 cursor-not-allowed'
                      : shareLoading || socialConnecting
                        ? btnLoading + ' cursor-wait'
                        : btnBg + ' ' + btnHover
                  }`}
                >
                  {socialConnecting
                    ? 'Connecting…'
                    : shareLoading
                      ? 'Posting…'
                      : isConnected
                        ? `Post to ${platformLabel}`
                        : 'Connect & Post'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

    </div>
  );
}
